import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useToast } from '../../hooks/useToast';
import Button from '../../components/common/Button';
import { useAuth } from '../../hooks/useAuth';
import { canViewAgencies } from '../../utils/agencyAccess';
import Input from '../../components/common/Input';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';
import IntakeFormEditorModal from '../../components/admin/IntakeFormEditorModal';
import ProductOfferEditorModal from '../../components/admin/ProductOfferEditorModal';
import ListLoadError from '../../components/admin/ListLoadError';
import api from '../../services/api';
import { listProductOffers } from '../../services/productOfferService';
import { fetchAllAdminProducts } from '../../services/productService';
import { getSupplyLabel, getSupplyTerm, toPerMonthRate } from '../../utils/supplyTerms';
import styles from './ProductsPage.module.css';
import { resolveLoadErrorMessage } from '../../utils/errorMessages';

// Card 53589: an entity load FAILURE must be distinguishable from a genuine zero.
const entityErrorStyle = {
  display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.375rem',
  color: 'var(--color-error)', fontSize: '0.75rem', fontWeight: 600,
};
const entityRetryStyle = {
  padding: '0.375rem 0.75rem', borderRadius: '6px',
  border: '0.5px solid var(--color-tan-400)', background: 'var(--color-tan-50)',
  color: 'var(--color-error)', fontSize: '0.75rem', fontWeight: 600,
  letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap',
  cursor: 'pointer', transition: 'background 0.2s ease, border-color 0.2s ease',
};

// Gradient presets for text
const GRADIENT_PRESETS = [
  { name: 'Sunset', value: 'linear-gradient(90deg, #f97316, #e11d48)' },
  { name: 'Ocean', value: 'linear-gradient(90deg, #2563eb, #06b6d4)' },
  { name: 'Forest', value: 'linear-gradient(90deg, #16a34a, #06b6d4)' },
  { name: 'Purple', value: 'linear-gradient(90deg, #7c3aed, #e11d48)' },
  { name: 'Gold', value: 'linear-gradient(90deg, #f59e0b, #f97316)' },
  { name: 'None', value: '' },
];

// Lazy-loaded Quill editor for rich text
const LazyQuill = ({ value, onChange }) => {
  const [QuillComponent, setQuillComponent] = useState(null);
  const [showGradientPicker, setShowGradientPicker] = useState(false);
  const quillRef = useState(null);

  useEffect(() => {
    import('react-quill-new').then((mod) => {
      const Quill = mod.default;
      // Register custom gradient format
      const Inline = Quill.Quill.import('blots/inline');
      class GradientBlot extends Inline {
        static create(value) {
          const node = super.create();
          node.style.background = value;
          node.style.webkitBackgroundClip = 'text';
          node.style.webkitTextFillColor = 'transparent';
          node.style.backgroundClip = 'text';
          node.dataset.gradient = value;
          return node;
        }
        static formats(node) {
          return node.dataset.gradient || node.style.background;
        }
      }
      GradientBlot.blotName = 'gradient';
      GradientBlot.tagName = 'span';
      GradientBlot.className = 'ql-gradient';
      Quill.Quill.register(GradientBlot);
      setQuillComponent(() => Quill);
    }).catch((err) => console.error('Failed to load Quill editor:', err));
    import('react-quill-new/dist/quill.snow.css').catch(() => {});
  }, []);

  const applyGradient = (gradient) => {
    const editor = quillRef[0]?.getEditor?.();
    if (!editor) return;
    const range = editor.getSelection();
    if (range && range.length > 0) {
      if (gradient) {
        editor.formatText(range.index, range.length, 'gradient', gradient);
      } else {
        editor.formatText(range.index, range.length, 'gradient', false);
      }
    }
    setShowGradientPicker(false);
  };

  const modules = useMemo(() => ({
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ color: ['#f97316', '#2d2d2d', '#737373', '#e11d48', '#16a34a', '#2563eb', false] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'blockquote'],
      ['clean']
    ]
  }), []);

  if (!QuillComponent) return <div className={styles.quillPlaceholder}>Loading editor...</div>;

  return (
    <div style={{ position: 'relative' }}>
      <div className={styles.gradientToolbar}>
        <button
          type="button"
          className={styles.gradientBtn}
          onClick={() => setShowGradientPicker(!showGradientPicker)}
          title="Apply Color Gradient to selected text"
        >
          Color Gradient
        </button>
        {showGradientPicker && (
          <div className={styles.gradientDropdown}>
            {GRADIENT_PRESETS.map((g) => (
              <button
                key={g.name}
                type="button"
                className={styles.gradientOption}
                onClick={() => applyGradient(g.value)}
              >
                {g.value ? (
                  <span style={{
                    background: g.value,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 600,
                  }}>{g.name}</span>
                ) : (
                  <span style={{ color: '#737373' }}>Remove</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      <QuillComponent
        ref={(el) => { quillRef[0] = el; }}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        className={styles.quillEditor}
      />
    </div>
  );
};


// Helper to find dosages missing NDC codes
const getMissingNdcDosages = (dosages) => {
  if (!dosages || dosages.length === 0) return [];
  return dosages.filter(d => !d.ndcCode);
};
// DrCare specialty/reason ID pairs per product category
const DRCARE_SPECIALTY_OPTIONS = [
  { label: 'Erectile Dysfunction', specialtyId: '9FAECD75-653F-4025-BD21-A43C5EEE3612', reasonId: '4bdcb59c-a5ac-4be4-a30d-b0185d34f941' },
  { label: 'Hair / Dermatology', specialtyId: 'C00CE8D0-75A2-4938-80C7-FD64D8D7269A', reasonId: 'c6e5eba0-4bb7-4374-b59b-4ba7d6302737' },
  { label: 'Skin (Tretinoin)', specialtyId: 'F7EB79D0-B009-4F08-AD16-3584DD981E25', reasonId: '26509bf9-82a7-4332-8a35-faa76b850262' },
  { label: 'Secondary Hypogonadism', specialtyId: '1C17F499-C915-476E-BA69-B50A9AF05C47', reasonId: '19f9c8f2-f491-4ef1-99be-bb5a5b55adb5' },
  { label: 'PCOS', specialtyId: '32803915-51D9-4D40-A366-ED920658F2B1', reasonId: '7e611daa-2cd4-4490-9d61-064445160ab3' },
  { label: 'Weight Loss', specialtyId: 'A094FF00-6A04-4C37-B666-D9A6C829CD1F', reasonId: 'acc3fb79-c169-4c4e-a293-76186fff8979' },
  { label: 'Nutritional Therapy', specialtyId: '0C396CC0-19EE-4F84-BF2E-DF530FC01510', reasonId: 'A1FFC7CC-7379-4700-902E-9C74346623C1' },
  { label: 'Detox & Antioxidant', specialtyId: '99DAF7A1-2324-4F69-B717-4220FFEF90F8', reasonId: '9A6CC99D-0A61-4439-A858-74194709447B' },
  { label: 'Growth Hormone Stimulation', specialtyId: '5B0E24C4-4E60-4D27-A947-03EA60E0FE59', reasonId: 'EB6732BB-A73B-46F6-8714-B29F5F002393' },
  { label: 'Metabolic Health', specialtyId: '45405281-A48E-4024-A451-9CF3518EB7BF', reasonId: 'BC66F3EC-BC1B-43E0-98BC-230FCC514D27' },
  { label: 'Neurometabolic Support', specialtyId: 'CAE13A53-67F4-456C-947B-978654B232DB', reasonId: 'DB730CDB-C0F6-4120-9AB4-BE8A746AA062' },
  { label: 'TRT (Testosterone)', specialtyId: '522f996f-4d48-4db2-a68c-9032af679941', reasonId: null },
  { label: 'Custom / Other', specialtyId: '', reasonId: '' },
];


// Compute per-tier starting-at prices from product dosages (pure function)
const getProductTierPrices = (product) => {
  const dosages = product?.dosages;
  if (!dosages || dosages.length === 0) return { access: null, one: null, plus: null };
  const result = { access: Infinity, one: Infinity, plus: Infinity };
  const tierKeys = ['access', 'one', 'plus'];
  let hasAny = false;
  for (const d of dosages) {
    if (d.isActive === false) continue;
    const sp = d.supplyPricing;
    const meta = d.supplyPricingMeta || {};
    if (sp && typeof sp === 'object') {
      for (const [moStr, tierPricing] of Object.entries(sp)) {
        if (!tierPricing || typeof tierPricing !== 'object') continue;
        if (meta[moStr]?.enabled === false) continue;
        for (const tier of tierKeys) {
          const cents = tierPricing[tier];
          if (cents && cents > 0 && cents < result[tier]) {
            result[tier] = cents;
            hasAny = true;
          }
        }
      }
    } else if (d.price && d.price > 0) {
      for (const tier of tierKeys) {
        if (d.price < result[tier]) {
          result[tier] = d.price;
          hasAny = true;
        }
      }
    }
  }
  if (!hasAny) return { access: null, one: null, plus: null };
  return {
    access: result.access === Infinity ? null : result.access,
    one: result.one === Infinity ? null : result.one,
    plus: result.plus === Infinity ? null : result.plus,
  };
};

// The starting-at prices the catalog actually advertises, straight from the server engine
// (startingAtService: basis-aware, honours manual mode). getProductTierPrices is the legacy
// client-side "cheapest across every duration" math, kept only as the fallback for
// STARTING_AT_BASIS_V2=false, where the transformer stops emitting startingAt.
const getStartingAtTierPrices = (product) =>
  product?.startingAt?.prices ?? getProductTierPrices(product);

const formatTierPrice = (cents) => {
  if (!cents) return '—';
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${Math.round(dollars)}` : `$${dollars.toFixed(2)}`;
};

// Cost rules mirror api/services/internalCostService.js — the sole source of the math.
// Keep in sync if the engine's multipliers ever change.
const COST_RULES = {
  medication: 'Internal cost, not the customer price. Charged per internal month of supply: 60 Day = 2 internal months, 3 Months = x3, 6 Months = x6, 12 Months = x12. Example: $40 on a 3 Months order costs $120. The customer PRICE does not follow this rule - see Supply Duration Pricing.',
  consultation: 'Charged per shipment, not per month: shipments = internal months divided by Max Months Per Shipment, rounded up. Example: a 6 Months order of a 3-month-max product ships twice, so $30 costs $60.',
  misc: 'Internal cost, not the customer price. Charged per internal month of supply, same multiplier as medication: 60 Day = 2 internal months, 3 Months = x3, 6 Months = x6, 12 Months = x12.',
  // NOT the internal per-shipment expense: this field is products.shipping_cost, which pricingEngine
  // bills the customer as the shipping fee. The internal shipping_and_handling_cost expense is only
  // settable per duration in the dosage supply pricing grid below.
  shipping: 'Customer-facing shipping fee, charged once per order and waived for tiers that include shipping. This is revenue, not an internal cost, and it is never multiplied by the supply term. The internal S&H expense is charged per shipment and is set per duration in the supply pricing grid.',
  maxMonths: 'Caps how much supply ships at once (cold-chain meds: 3; default 12). Shipments = internal months divided by this value, rounded up, and consultation + internal S&H costs are charged per shipment. 60 Day = 2 internal months.',
  override: 'Whole-term total for this duration: it REPLACES the calculated cost and is never multiplied by the supply term.'
};

// Price rules mirror api/services/pricingEngine.js, which computes medicationGross as
// adjustedPricePerMonth * supplyMonths where supplyMonths is the RAW storage key. So the "1"
// (60 Day) term is charged exactly ONCE. This is deliberately NOT the cost convention above:
// internalCostService scales costs by INTERNAL months (60 Day = 2) while the charge scales by
// the KEY (60 Day = 1). The two agree on 3/6/12 and differ only on 60 Day, which is why the
// mismatch survived review. Keep in sync if either engine's multiplier changes.
const PRICE_RULES = {
  supplyDuration: 'The 3 Months, 6 Months and 12 Months fields each take a PER-MONTH rate and are charged that many times. The 60 Day field is the exception: it is charged ONCE, exactly as entered, so it must hold the FULL two-month total. Example: Semaglutide Pill is $99/mo, so its 60 Day field holds 19800 ($198), not 9900. Internal costs do NOT follow this rule - they scale by internal months, where 60 Day counts as 2.'
};

// Info affordance for the cost-multiplication rules. Pure CSS (no state): the tip shows on hover and
// on focus, so it is tappable on touch and reachable by keyboard. Anchored to the field, not the icon,
// so it cannot overflow the modal's horizontally-clipping body.
const CostInfo = ({ label, tip }) => (
  <>
    <button
      type="button"
      className={styles.costInfoBtn}
      aria-label={`${label}: ${tip}`}
    >
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none" />
        <circle cx="8" cy="4.6" r="0.95" fill="currentColor" />
        <rect x="7.25" y="6.9" width="1.5" height="5" rx="0.75" fill="currentColor" />
      </svg>
    </button>
    <span className={styles.costTip} role="tooltip">{tip}</span>
  </>
);

// Starting-at config mirrors api/services/startingAtService.js (STARTING_AT_BASES /
// DEFAULT_STARTING_AT_BASIS / TIER_TYPES) — the engine that actually computes these prices.
// Keep in sync if the engine's enums change.
const DEFAULT_STARTING_AT_BASIS = '3_month';
const STARTING_AT_BASIS_OPTIONS = [
  { value: '60_day', label: '60 Day' },
  { value: '3_month', label: '3 Month' },
  { value: '6_month', label: '6 Month' },
  { value: '12_month', label: '12 Month' },
  { value: 'all', label: 'All' }
];
const STARTING_AT_MODE_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'manual', label: 'Manual' }
];
const STARTING_AT_TIERS = [
  { key: 'access', label: 'Access' },
  { key: 'one', label: 'ONE' },
  { key: 'plus', label: 'PLUS' }
];
const getBasisLabel = (basis) =>
  STARTING_AT_BASIS_OPTIONS.find(({ value }) => value === basis)?.label ?? basis;

// === Per-tier price modifier (Aug 4, 2026 — Task 17) ===================================
// A membership tier can carry a product_price_modifier that changes what is actually billed,
// so a rate typed into the Supply Duration Pricing grid is NOT what that tier pays. These
// helpers resolve the real modifier out of custom_membership_tiers so the grid can show the
// resulting rate. The +10% Oval Access modifier is INTENDED (P. ruled 2026-07-15) — this is a
// display, never a correction.
const PRICE_MODIFIER_TIER_TYPES = ['access', 'one', 'plus'];

// base_name ("Oval Access") -> tier type ("access"), or null.
const getTierTypeFromBaseName = (baseName) => {
  const name = String(baseName ?? '').toLowerCase();
  return PRICE_MODIFIER_TIER_TYPES.find((tier) => name.includes(tier)) ?? null;
};

// tier type -> { modifier, tierName }, mirroring api/services/startingAtService.js
// buildTierModifierMap: inactive rows, unrecognised base_names and unparseable modifiers are
// SKIPPED (never defaulted), and each base_name keeps its FIRST match. Same rule as the engine,
// so this badge cannot quote a modifier the engine would not apply. Absent tier = no modifier.
const buildTierModifierMap = (tiers) =>
  (Array.isArray(tiers) ? tiers : []).reduce((acc, tier) => {
    if ((tier?.isActive ?? tier?.is_active) === false) return acc;
    const tierType = getTierTypeFromBaseName(tier?.baseName ?? tier?.base_name);
    if (tierType === null || acc[tierType] !== undefined) return acc;
    const modifier = Number(tier?.productPriceModifier ?? tier?.product_price_modifier);
    return Number.isFinite(modifier)
      ? { ...acc, [tierType]: { modifier, tierName: String(tier?.name ?? '') } }
      : acc;
  }, {});

// "+10%" / "+7.5%" / "-5%". product_price_modifier is numeric(5,4), so x100 never needs more
// than 2 decimals; Number() drops the trailing zeros a fixed toFixed(2) would leave behind.
const formatModifierPercent = (modifier) =>
  `${modifier > 0 ? '+' : ''}${Number((modifier * 100).toFixed(2))}%`;

// The rate the member is actually BILLED: Math.round(raw x (1 + modifier)) — the same rounding
// as pricingEngine.js:151 and productTransformer.js buildSupplyPricingEffective, so this figure
// agrees with the charge to the cent. Deliberately NOT startingAtService's toEffectiveRate,
// which ceils to whole dollars (8690 -> 8700) for the catalog's "starting at" scalar.
const applyPriceModifier = (cents, modifier) => Math.round(cents * (1 + modifier));

// The auto prices for a basis the operator has selected but not saved. The SERVER quotes them
// (GET /admin/products/:id?startingAtBasis=) so the modal and the catalog stay one engine, not two.
//
// `quotedBasis` is the basis the server ECHOED (startingAt.basis = the one we asked for, except
// under the kill switch) and doubles as the "a quote came back" sentinel. `signal` is the whole
// startingAt object, kept so the disclosure below can read the engine's provenance fields without
// this shape having to grow a field every time the engine gains one.
const EMPTY_BASIS_PREVIEW = { auto: null, quotedBasis: null, signal: null, loading: false, error: null };

// A basis the tiers disagree on is reported as `all` by the engine — it means "cheapest across
// every duration", not a term the product sells, so it must never be named like one.
const describeQuotedBasis = (startingAt) =>
  startingAt.quotedBasis === 'all'
    ? 'the cheapest term available'
    : startingAt.quotedBasisLabel ?? getBasisLabel(startingAt.quotedBasis);

// What to disclose about the prices on screen, or null when they really did come from the pill
// the operator lit. resolveStartingAt (api/services/startingAtService.js) distinguishes three
// situations and they get three DIFFERENT messages — collapsing them is how the kill-switch copy
// ended up explaining a data-driven fallback it has nothing to do with:
//   1. kill switch — STARTING_AT_BASIS_V2=false, the server ignored the request and answered
//      with another basis entirely. Detected as before: the echoed basis is not the requested one.
//   2. no rate     — basisHasRate false: the product carries no supply price for this term at all.
//   3. fallback    — basisFallback true (with a rate present): SOME tiers were substituted.
const deriveBasisDisclosure = (startingAt, requestedBasis) => {
  if (!startingAt) return null;
  const requestedLabel = getBasisLabel(requestedBasis);
  const echoedBasis = startingAt.basis ?? requestedBasis;
  if (echoedBasis !== requestedBasis) {
    return `Quoted from ${getBasisLabel(echoedBasis)} - per-basis quoting is currently disabled.`;
  }
  if (startingAt.basisHasRate === false) {
    // No rate here AND no substitution means no rate anywhere — the tiers below read "No pricing",
    // and pointing at a term the engine never quoted from would be a fresh lie in place of the old one.
    return startingAt.basisFallback === true
      ? `This product has no ${requestedLabel} supply price, so it does not sell that term. The rates above are quoted from ${describeQuotedBasis(startingAt)} instead.`
      : `This product has no ${requestedLabel} supply price, and no rate on any other term to quote from either.`;
  }
  if (startingAt.basisFallback === true) {
    return `Some tiers have no ${requestedLabel} supply price. Those rates are quoted from ${describeQuotedBasis(startingAt)} instead.`;
  }
  return null;
};
const STARTING_AT_RULES = {
  mode: 'Auto quotes the cheapest monthly rate the supply pricing below actually offers for the chosen basis, recalculated on every save. Manual quotes exactly what you type here — no tier modifier, no promo — until you switch back to Auto.',
  basis: 'Which supply term the advertised "starting at" rate is quoted from. Longer terms carry a volume discount, so the basis changes the number the customer sees. All quotes the cheapest term available. Default: 3 Month.'
};

// Money fields in this form are held as dollar strings and converted at the edges only.
const centsToDollarInput = (cents) => {
  const value = cents ?? '';
  return value === '' ? '' : (value / 100).toFixed(2);
};
// Returns integer cents, or null for anything not a usable amount.
const dollarInputToCents = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed * 100) : null;
};

// Stored manual prices are per-tier cents; the form holds dollar strings.
const manualCentsToForm = (manual) =>
  STARTING_AT_TIERS.reduce(
    (acc, { key }) => ({ ...acc, [key]: centsToDollarInput(manual?.[key]) }),
    {}
  );

// null clears the override entirely (per the Joi contract) — an all-empty form means "no manual prices".
const manualFormToCents = (form) => {
  const cents = STARTING_AT_TIERS.reduce(
    (acc, { key }) => ({ ...acc, [key]: dollarInputToCents(form?.[key]) }),
    {}
  );
  return STARTING_AT_TIERS.some(({ key }) => cents[key] !== null) ? cents : null;
};

const AdminProductsPage = () => {
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [intakeForms, setIntakeForms] = useState([]);
  const [productOffers, setProductOffers] = useState({});
  const [loading, setLoading] = useState(true);
  const [categoryClickGoesToViewAll, setCategoryClickGoesToViewAll] = useState(true);
  const [savingCatalogSetting, setSavingCatalogSetting] = useState(false);

  // Product modal state
  const [productModalOpen, setProductModalOpen] = useState(false);
  const { hasPermission } = useAuth();
  const canEditProducts = hasPermission('products.edit');
  const canCreateProducts = hasPermission('products.create');
  const canDeleteProducts = hasPermission('products.delete');
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    slug: '',
    shortDescription: '',
    category: '',
    isActive: true,
    isPrescriptionRequired: true,
    lifestyleImageUrl: '',
    isHidden: false,
    isComingSoon: false,
    isOutOfStock: false,
    isControlledSubstance: false,
    maxSupplyMonths: 12,
    consultationFee: '',
    increasedStrengthFee: '',
    basePrice: '',
    retailPrice: '',
    shippingCost: '',
    medicationCost: '',
    consultationCost: '',
    miscCost: '',
    startingAtMode: 'auto',
    startingAtBasis: DEFAULT_STARTING_AT_BASIS,
    startingAtManual: { access: '', one: '', plus: '' },
    drcareSpecialtyId: '',
    drcareReasonId: '',
    syringeCode: '',
    syringeDescription: '',
    providerType: 'DrCare',
    doctorNowConfig: { encounterTypeId: '', consultType: 'Async', requiresAppointment: false, sku: '', productId: '', packageId: '', intakeFormId: '', plans: {} },
    pharmacyName: ''
  });

  // Category modal state
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    slug: '',
    shortDescription: '',
    icon: '',
    isActive: true,
    intakeFormId: ''
  });

  // Image upload state
  const [uploadingImages, setUploadingImages] = useState(false);
  const [productImages, setProductImages] = useState([]);
  const [uploadingCategoryImage, setUploadingCategoryImage] = useState(false);
  const [uploadingLifestyleImage, setUploadingLifestyleImage] = useState(false);

  // Dosage management state
  const [productDosages, setProductDosages] = useState([]);
  // Guards the create-a-dosage POST. The ref is the real guard because it updates
  // synchronously, so a rapid double-click cannot slip a second request through before
  // React has re-rendered the disabled button; `addingDosage` drives that disabled state.
  const addDosageInFlight = useRef(false);
  const [addingDosage, setAddingDosage] = useState(false);
  const [loadingDosages, setLoadingDosages] = useState(false);
  // A FAILED dosage load must stay DISTINGUISHABLE from a product that genuinely has no
  // dosages. `productDosages` is not only rendered -- it is read by WRITE paths (the
  // basePrice assembly in handleProductSubmit, and isDefault/displayOrder in
  // handleAddDosage) -- so an empty array left behind by a failure would silently act on
  // a list we never actually read.
  const [dosagesError, setDosagesError] = useState(null);
  // Tier toggle for supply duration pricing (shows one tier at a time)
  const [supplyPricingTier, setSupplyPricingTier] = useState("access");

  // Server-quoted starting-at prices for an unsaved basis (see EMPTY_BASIS_PREVIEW)
  const [basisPreview, setBasisPreview] = useState(EMPTY_BASIS_PREVIEW);

  // System promo state
  const [systemPromos, setSystemPromos] = useState([]);
  const [loadingSystemPromos, setLoadingSystemPromos] = useState(false);
  // Same defect on the promo control: with an empty list the <select> has no <option>
  // matching the product's assigned promo, so a product that HAS one renders as
  // "No promo assigned".
  const [systemPromosError, setSystemPromosError] = useState(null);

  // Promo conflict error state
  const [promoConflictError, setPromoConflictError] = useState(null);

  // Syringe management state
  const [syringes, setSyringes] = useState([]);
  const [loadingSyringes, setLoadingSyringes] = useState(false);
  const [syringesError, setSyringesError] = useState(null);
  const [syringeModalOpen, setSyringeModalOpen] = useState(false);
  const [editingSyringe, setEditingSyringe] = useState(null);
  const [syringeForm, setSyringeForm] = useState({ code: '', description: '', fulfillmentQuantity: 1, isActive: true });

  // Config tab state — controls which network's settings are visible (independent of active provider)
  const [configTab, setConfigTab] = useState('DrCare');

  // Super Offer modal state

  // Product Offer modal state
  const [productOfferModalOpen, setProductOfferModalOpen] = useState(false);
  const [editingProductForOffer, setEditingProductForOffer] = useState(null);

  // Intake Form modal state
  const [intakeModalOpen, setIntakeModalOpen] = useState(false);
  const [editingIntakeProduct, setEditingIntakeProduct] = useState(null);

  // Info Popup editor state
  const [infoPopupModalOpen, setInfoPopupModalOpen] = useState(false);
  const [infoPopupProduct, setInfoPopupProduct] = useState(null);
  const [infoPopupForm, setInfoPopupForm] = useState({
    detailedDescription: '',
    lifestyleImageUrl: '',
    isHidden: false,
    isComingSoon: false,
    consultationFee: '',
    basePrice: '',
    retailPrice: '',
    shippingCost: '',
    drcareSpecialtyId: '',
    drcareReasonId: '',
    syringeCode: '',
    syringeDescription: ''
  });
  const [savingInfoPopup, setSavingInfoPopup] = useState(false);

  // Membership Tiers state
  const [tiers, setTiers] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(false);
  const [tiersError, setTiersError] = useState(null);
  const [tierModalOpen, setTierModalOpen] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [dragTierFeatureIdx, setDragTierFeatureIdx] = useState(null);
  const [tierForm, setTierForm] = useState({
    name: '',
    price: '',
    description: '',
    features: []
  });

  // Custom Membership Tiers state
  const [customTiers, setCustomTiers] = useState([]);
  const [loadingCustomTiers, setLoadingCustomTiers] = useState(false);
  const [customTiersError, setCustomTiersError] = useState(null);
  const [customTierModalOpen, setCustomTierModalOpen] = useState(false);
  const [editingCustomTier, setEditingCustomTier] = useState(null);
  const [dragCustomFeatureIdx, setDragCustomFeatureIdx] = useState(null);
  const [econCategoryFilter, setEconCategoryFilter] = useState('');
  const [customTierForm, setCustomTierForm] = useState({
    baseName: '',
    planType: 'member',
    agencyName: '',
    monthlyPrice: '',
    revenue: '',
    consultationFeeIncluded: true,
    shippingHandlingIncluded: true,
    productPriceModifier: '0',
    features: [],
    isActive: true,
    productEconomics: []
  });
  const [entities, setEntities] = useState([]);
  const [entitiesError, setEntitiesError] = useState(false);

  // Card 53589: the entity picker is a WRITE control on a `required` field, so a
  // failed load must not be rendered as "No entities available" - that asserts a
  // fact about the data and hard-blocks tier creation with the wrong cause given.
  const loadEntities = useCallback(async () => {
    setEntitiesError(false);
    // Card 49621: only fetch what this admin may read; otherwise the entity
    // picker stays empty rather than costing a guaranteed 403. A PREDICTED 403
    // is not a load failure, so this arm stays SILENT (card 53589).
    if (!canViewAgencies(hasPermission)) {
      setEntities([]);
      return;
    }
    try {
      const response = await api.get('/admin/agencies');
      setEntities(response.agencies || []);
    } catch {
      setEntities([]);
      setEntitiesError(true);
    }
  }, [hasPermission]);

  useEffect(() => {
    loadData();
    loadTiers();
    loadCustomTiers();
    loadSyringes();
    loadCatalogSetting();
  }, []);

  // Quote a basis the operator picked but has not saved, so the tier rows move the moment a pill is
  // clicked. The server owns the math; this only asks for it.
  //
  // Keyed on the REQUESTED basis, never on the one the server answers with: STARTING_AT_BASIS_V2=false
  // makes the server answer `all` to every basis, so an effect that re-fired while requested !== quoted
  // would loop forever against the kill switch.
  useEffect(() => {
    const productId = editingProduct?._id || editingProduct?.id;
    const savedBasis = editingProduct?.startingAtBasis || DEFAULT_STARTING_AT_BASIS;
    const requestedBasis = productForm.startingAtBasis;
    const isAuto = productForm.startingAtMode === 'auto';

    // Manual mode advertises the operator's own numbers, and the saved basis is already quoted on
    // editingProduct.startingAt — neither needs a preview.
    if (!productModalOpen || !productId || !isAuto || requestedBasis === savedBasis) {
      setBasisPreview(EMPTY_BASIS_PREVIEW);
      return;
    }

    let cancelled = false;
    // Keep the last good prices on screen while the new ones load; blanking them reads as $0.
    setBasisPreview(prev => ({ ...prev, loading: true, error: null }));

    api.get(`/admin/products/${productId}?startingAtBasis=${encodeURIComponent(requestedBasis)}`)
      .then(res => {
        if (cancelled) return;
        const startingAt = res?.product?.startingAt;
        setBasisPreview({
          auto: startingAt?.auto ?? null,
          quotedBasis: startingAt?.basis ?? null,
          signal: startingAt ?? null,
          loading: false,
          error: null
        });
      })
      .catch(err => {
        if (cancelled) return;
        // Hold the last good values and say what happened — never blank the prices on a failure.
        setBasisPreview(prev => ({
          ...prev,
          loading: false,
          error: err?.message || 'request failed'
        }));
      });

    // Clicking 60 Day then 6 Month in quick succession must not let the slower response win.
    return () => { cancelled = true; };
  }, [productModalOpen, editingProduct, productForm.startingAtBasis, productForm.startingAtMode]);

  const loadCatalogSetting = async () => {
    try {
      const res = await api.get('/admin/settings/catalog.categoryClickGoesToViewAll');
      const val = res?.value;
      setCategoryClickGoesToViewAll(val === 'true' || val === true);
    } catch {
      // Default to true if setting doesn't exist
      setCategoryClickGoesToViewAll(true);
    }
  };

  const handleToggleCatalogSetting = async () => {
    const newValue = !categoryClickGoesToViewAll;
    setSavingCatalogSetting(true);
    try {
      await api.put('/admin/settings/catalog.categoryClickGoesToViewAll', { value: String(newValue) });
      setCategoryClickGoesToViewAll(newValue);
      toast.success('Catalog setting updated');
    } catch {
      toast.error('Failed to update setting');
    } finally {
      setSavingCatalogSetting(false);
    }
  };

    const loadData = async () => {
    try {
      const [productsRes, categoriesRes, intakeFormsRes, offersRes] = await Promise.all([
        fetchAllAdminProducts(),
        api.get('/admin/categories'),
        api.get('/admin/intake-forms').catch(() => ({ forms: [] })),
        listProductOffers().catch(() => ({ offers: [] }))
      ]);
      setProducts(productsRes.products || []);
      setCategories(categoriesRes.categories || []);
      setIntakeForms(intakeFormsRes.forms || []);
      // Build productId -> offer lookup map
      const offersMap = {};
      (offersRes.offers || []).forEach(offer => {
        if (offer.productId) offersMap[offer.productId] = offer;
      });
      setProductOffers(offersMap);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const loadTiers = async () => {
    setLoadingTiers(true);
    try {
      const res = await api.get('/admin/membership-tiers');
      setTiers(res.tiers || []);
      setTiersError(null);
    } catch (error) {
      console.error('Failed to load tiers:', error);
      setTiersError('Could not load membership tiers');
    } finally {
      setLoadingTiers(false);
    }
  };

  const loadCustomTiers = async () => {
    setLoadingCustomTiers(true);
    try {
      const res = await api.get('/admin/custom-membership-tiers');
      setCustomTiers(res.tiers || []);
      setCustomTiersError(null);
    } catch (error) {
      console.error('Failed to load custom tiers:', error);
      setCustomTiersError('Could not load custom membership tiers');
    } finally {
      setLoadingCustomTiers(false);
    }
  };

  const loadSyringes = async () => {
    setLoadingSyringes(true);
    try {
      const res = await api.get('/admin/syringes');
      setSyringes(res.syringes || []);
      setSyringesError(null);
    } catch (error) {
      console.error('Failed to load syringes:', error);
      setSyringesError('Could not load syringes');
    } finally {
      setLoadingSyringes(false);
    }
  };

  const handleOpenSyringeModal = (syringe = null) => {
    if (syringe) {
      setEditingSyringe(syringe);
      setSyringeForm({
        code: syringe.code || '',
        description: syringe.description || '',
        fulfillmentQuantity: syringe.fulfillmentQuantity ?? 1,
        isActive: syringe.isActive !== false
      });
    } else {
      setEditingSyringe(null);
      setSyringeForm({ code: '', description: '', fulfillmentQuantity: 1, isActive: true });
    }
    setSyringeModalOpen(true);
  };

  const handleSyringeSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSyringe) {
        await api.patch('/admin/syringes/' + editingSyringe.id, syringeForm);
        toast.success('Syringe updated');
      } else {
        await api.post('/admin/syringes', syringeForm);
        toast.success('Syringe created');
      }
      setSyringeModalOpen(false);
      loadSyringes();
    } catch (error) {
      toast.error(error.message || 'Failed to save syringe');
    }
  };

  const handleDeleteSyringe = async (syringeId) => {
    if (!canDeleteProducts) return;
    if (!confirm('Delete this syringe?')) return;
    try {
      await api.delete('/admin/syringes/' + syringeId);
      toast.success('Syringe deleted');
      loadSyringes();
    } catch (error) {
      toast.error('Failed to delete syringe');
    }
  };

  // ── Per-tier product pricing overrides ──────────────────────────────────────
  // Two stores answer "does this tier bill this product at retail?", and only one of
  // them is ever read by a pricing path:
  //   • `custom_membership_tiers.product_economics` — the tier PUT body. pricingEngine
  //     and productTransformer read it for `void` ONLY; a `retail` entry there is inert.
  //   • `tier_product_overrides` — GET/PUT /custom-membership-tiers/:id/product-overrides.
  //     This is the one checkout resolves a retail price from.
  // The modal keeps editing `productEconomics` (it also carries `void`, which the other
  // store has no column for) and mirrors just the `retail` half into the override table,
  // so the button an admin clicks reaches the store that actually prices.
  const toRetailOverrides = (economics = []) => economics
    .filter(({ pricingType }) => pricingType === 'retail')
    .map(({ productId }) => ({ productId, useRetailPrice: true }));

  // Both prices are shown on every row, not just the selected one: the choice between
  // "Modifier" and "Retail" is a choice between two numbers, and an admin cannot make it
  // while only one of them is on screen. Prices are cents; 0/null render as an em dash
  // rather than "$0.00", which would read as a real free price.
  const formatEconPrice = (cents) => (typeof cents === 'number' && cents > 0 ? `$${(cents / 100).toFixed(2)}` : '—');

  // `startingAtPrice` is the dosage-derived headline; `basePrice` is what a product with no
  // dosages charges. Same precedence the product list itself uses.
  const deriveEconOvalPrice = (product) => product?.startingAtPrice ?? product?.basePrice ?? null;

  // Pure. Override rows win over the tier's inline economics for the products they name;
  // a `void` entry on any other product is preserved untouched.
  const mergeRetailOverrides = (economics = [], overrides = []) => {
    const retailIds = overrides.filter(({ useRetailPrice }) => useRetailPrice === true).map(({ productId }) => productId);
    return [
      ...economics.filter(({ productId }) => !retailIds.includes(productId)),
      ...retailIds.map(productId => ({ productId, pricingType: 'retail' }))
    ];
  };

  const loadTierProductOverrides = async (tierId) => {
    try {
      const res = await api.get(`/admin/custom-membership-tiers/${tierId}/product-overrides`);
      setCustomTierForm(prev => ({ ...prev, productEconomics: mergeRetailOverrides(prev.productEconomics, res.overrides || []) }));
    } catch {
      // A tier with no override rows is the normal case, and a failed read must not
      // block editing the rest of the tier — the inline economics still render.
    }
  };

  // Bulk set every product currently visible under the category filter.
  const handleSetEconomicsForProducts = (productList, pricingType) => {
    const ids = productList.map(p => p.id || p._id);
    setCustomTierForm(prev => {
      const untouched = prev.productEconomics.filter(({ productId }) => !ids.includes(productId));
      return {
        ...prev,
        productEconomics: pricingType === 'modifier'
          ? untouched
          : [...untouched, ...ids.map(productId => ({ productId, pricingType }))]
      };
    });
  };

  const handleOpenCustomTierModal = (tier = null) => {
    if (tier) {
      setEditingCustomTier(tier);
      setCustomTierForm({
        baseName: tier.baseName || tier.name || '',
        planType: tier.planType || 'member',
        agencyName: tier.agencyName || '',
        monthlyPrice: tier.monthlyPrice !== undefined ? (tier.monthlyPrice / 100).toFixed(2) : '',
        revenue: tier.revenue !== undefined && tier.revenue !== null ? tier.revenue.toString() : '',
        consultationFeeIncluded: tier.consultationFeeIncluded !== false,
        shippingHandlingIncluded: tier.shippingHandlingIncluded !== false,
        productPriceModifier: tier.productPriceModifier !== undefined ? (tier.productPriceModifier * 100).toString() : '0',
        features: tier.features || [],
        isActive: tier.isActive !== false,
        productEconomics: tier.productEconomics || []
      });
    } else {
      setEditingCustomTier(null);
      setCustomTierForm({
        baseName: '', planType: 'member', agencyName: '', monthlyPrice: '', revenue: '', consultationFeeIncluded: true,
        shippingHandlingIncluded: true, productPriceModifier: '0', features: [], isActive: true,
        productEconomics: []
      });
    }
    setEconCategoryFilter('');
    setCustomTierModalOpen(true);
    if (tier?.id) loadTierProductOverrides(tier.id);
    loadEntities();
  };

  const handleCustomTierSubmit = async (e) => {
    e.preventDefault();
    const data = {
      baseName: customTierForm.baseName,
      planType: customTierForm.planType,
      agencyName: customTierForm.agencyName || null,
      monthlyPrice: Math.round(parseFloat(customTierForm.monthlyPrice || 0) * 100),
      revenue: customTierForm.revenue !== '' ? parseFloat(customTierForm.revenue) : null,
      consultationFeeIncluded: customTierForm.consultationFeeIncluded,
      shippingHandlingIncluded: customTierForm.shippingHandlingIncluded,
      productPriceModifier: parseFloat(customTierForm.productPriceModifier || 0) / 100,
      features: customTierForm.features,
      isActive: customTierForm.isActive,
      productEconomics: customTierForm.productEconomics
    };
    try {
      // The tier write comes first: on create there is no id to hang overrides off until
      // the row exists, and on update a rejected tier body must not leave the override
      // table describing a tier that was never saved.
      const saveTier = async () => {
        if (editingCustomTier) {
          await api.put('/admin/custom-membership-tiers/' + editingCustomTier.id, data);
          return editingCustomTier.id;
        }
        const created = await api.post('/admin/custom-membership-tiers', data);
        return created?.tier?.id;
      };
      const savedTierId = await saveTier();

      // Mirror the retail selections into `tier_product_overrides`, the store checkout
      // prices from. This PUT is a REPLACE, so clearing a product back to "Modifier"
      // removes its row.
      if (savedTierId) {
        await api.put(`/admin/custom-membership-tiers/${savedTierId}/product-overrides`, {
          overrides: toRetailOverrides(customTierForm.productEconomics)
        });
      }
      toast.success(editingCustomTier ? 'Custom tier updated' : 'Custom tier created');
      setCustomTierModalOpen(false);
      loadCustomTiers();
    } catch (error) {
      toast.error(error.message || 'Failed to save custom tier');
    }
  };

  const handleDeleteCustomTier = async (tierId) => {
    if (!confirm('Delete this custom tier?')) return;
    try {
      await api.delete('/admin/custom-membership-tiers/' + tierId);
      toast.success('Custom tier deleted');
      loadCustomTiers();
    } catch (error) {
      toast.error('Failed to delete custom tier');
    }
  };

  const handleAddCustomFeature = () => {
    setCustomTierForm(prev => ({
      ...prev,
      features: [...prev.features, { featureName: '', isIncluded: true, displayOrder: prev.features.length }]
    }));
  };

  const handleUpdateCustomFeature = (index, field, value) => {
    setCustomTierForm(prev => ({
      ...prev,
      features: prev.features.map((f, i) => i === index ? { ...f, [field]: value } : f)
    }));
  };

  const handleRemoveCustomFeature = (index) => {
    setCustomTierForm(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleOpenTierModal = (tier = null) => {
    if (tier) {
      setEditingTier(tier);
      setTierForm({
        name: tier.name || '',
        price: tier.price !== undefined ? (tier.price / 100).toFixed(2) : '',
        description: tier.description || '',
        features: tier.features || []
      });
    } else {
      setEditingTier(null);
      setTierForm({ name: '', price: '', description: '', features: [] });
    }
    setTierModalOpen(true);
  };

  const handleTierSubmit = async (e) => {
    e.preventDefault();
    const data = {
      name: tierForm.name,
      price: Math.round(parseFloat(tierForm.price || 0) * 100),
      description: tierForm.description,
      features: tierForm.features.map((f, i) => ({
        featureName: f.featureName,
        isIncluded: f.isIncluded,
        displayOrder: i
      }))
    };

    try {
      if (editingTier) {
        await api.put(`/admin/membership-tiers/${editingTier.id}`, data);
        toast.success('Tier updated');
      } else {
        data.displayOrder = tiers.length;
        await api.post('/admin/membership-tiers', data);
        toast.success('Tier created');
      }
      setTierModalOpen(false);
      loadTiers();
    } catch (error) {
      toast.error(error.message || 'Failed to save tier');
    }
  };

  const handleDeleteTier = async (tierId) => {
    try {
      await api.delete(`/admin/membership-tiers/${tierId}`);
      toast.success('Tier removed');
      loadTiers();
    } catch (error) {
      toast.error('Failed to delete tier');
    }
  };

  const handleAddFeature = () => {
    setTierForm(prev => ({
      ...prev,
      features: [...prev.features, { featureName: '', isIncluded: true, displayOrder: prev.features.length }]
    }));
  };

  const handleUpdateFeature = (index, field, value) => {
    setTierForm(prev => ({
      ...prev,
      features: prev.features.map((f, i) => i === index ? { ...f, [field]: value } : f)
    }));
  };

  const handleRemoveFeature = (index) => {
    setTierForm(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  // Product handlers

  // Derive product status from boolean flags
  const deriveProductStatus = (form) =>
    form.isHidden ? 'hidden' :
    form.isComingSoon ? 'coming_soon' :
    form.isOutOfStock ? 'out_of_stock' :
    'active';

  // Convert status string to boolean flags
  const statusToBooleans = (status) => ({
    isHidden: status === 'hidden',
    isComingSoon: status === 'coming_soon',
    isOutOfStock: status === 'out_of_stock'
  });

  // Status display config
  const STATUS_CONFIG = {
    active: { label: 'Active', className: 'statusBadgeActive' },
    coming_soon: { label: 'Coming Soon', className: 'statusBadgeComingSoon' },
    out_of_stock: { label: 'Out of Stock', className: 'statusBadgeOutOfStock' },
    hidden: { label: 'Hidden', className: 'statusBadgeHidden' }
  };

  // Named so the error states can offer a retry that re-runs EXACTLY this load.
  const loadProductDosages = async (product) => {
    if (!product) return;
    setLoadingDosages(true);
    setDosagesError(null);
    try {
      const productId = product._id || product.id;
      const response = await api.get(`/admin/products/${productId}/dosages`);
      setProductDosages(response.dosages || []);
    } catch (error) {
      console.error('Failed to load dosages:', error);
      setProductDosages([]);
      setDosagesError(resolveLoadErrorMessage(error, 'We could not load this product\u2019s dosages.'));
    } finally {
      setLoadingDosages(false);
    }
  };

  const loadSystemPromos = async () => {
    setLoadingSystemPromos(true);
    setSystemPromosError(null);
    try {
      const promoResponse = await api.get('/admin/promos/system');
      setSystemPromos(promoResponse.promoCodes || promoResponse.promos || []);
    } catch (err) {
      console.error('Failed to load system promos:', err);
      setSystemPromos([]);
      setSystemPromosError(resolveLoadErrorMessage(err, 'We could not load the system promos.'));
    } finally {
      setLoadingSystemPromos(false);
    }
  };

  const handleOpenProductModal = async (product = null) => {
    console.log("[DEBUG] handleOpenProductModal called", { product, categoriesLength: categories.length });
    if (product) {
      setEditingProduct(product);
      // Extract category ID from various formats (populated object or string)
      const categoryId = product.category?.id || product.category?._id ||
        (typeof product.category === 'string' ? product.category : '');
      setProductForm({
        name: product.name || '',
        slug: product.slug || '',
        shortDescription: product.shortDescription || '',
        category: categoryId,
        isActive: product.isActive !== false,
        isPrescriptionRequired: product.isPrescriptionRequired !== false,
        lifestyleImageUrl: product.lifestyleImageUrl || '',
        isHidden: product.isHidden === true,
        isComingSoon: product.isComingSoon === true,
        isOutOfStock: product.isOutOfStock === true,
        isControlledSubstance: product.isControlledSubstance === true,
        maxSupplyMonths: product.maxSupplyMonths || 12,
        consultationFee: product.consultationFee != null ? (product.consultationFee / 100).toFixed(2) : '',
        increasedStrengthFee: (product.increasedStrengthFee ?? product.increased_strength_fee) != null ? ((product.increasedStrengthFee ?? product.increased_strength_fee) / 100).toFixed(2) : '',
        basePrice: product.basePrice != null ? (product.basePrice / 100).toFixed(2) : '',
        retailPrice: product.retailPrice != null ? (product.retailPrice / 100).toFixed(2) : '',
        shippingCost: product.shippingCost != null ? (product.shippingCost / 100).toFixed(2) : '',
        medicationCost: product.medicationCost != null ? (product.medicationCost / 100).toFixed(2) : '',
        consultationCost: product.consultationCost != null ? (product.consultationCost / 100).toFixed(2) : '',
        miscCost: product.miscCost != null ? (product.miscCost / 100).toFixed(2) : '',
        startingAtMode: product.startingAtMode === 'manual' ? 'manual' : 'auto',
        startingAtBasis: product.startingAtBasis || DEFAULT_STARTING_AT_BASIS,
        startingAtManual: manualCentsToForm(product.startingAtManual),
        drcareSpecialtyId: product.drcareSpecialtyId || '',
        drcareReasonId: product.drcareReasonId || '',
        syringeCode: product.syringeCode || '',
        syringeDescription: product.syringeDescription || '',
        providerType: product.providerType || 'DrCare',
        doctorNowConfig: {
          encounterTypeId: product.doctorNowConfig?.encounterTypeId || '',
          consultType: product.doctorNowConfig?.consultType || 'Async',
          requiresAppointment: product.doctorNowConfig?.requiresAppointment ?? false,
          sku: product.doctorNowConfig?.sku || '',
          productId: product.doctorNowConfig?.productId ?? product.doctorNowConfig?.product_id ?? '',
          packageId: product.doctorNowConfig?.packageId ?? product.doctorNowConfig?.package_id ?? '',
          intakeFormId: product.doctorNowConfig?.intakeFormId || '',
          plans: (() => {
            const rawPlans = product.doctorNowConfig?.plans ?? product.doctor_now_config?.plans ?? {};
            const normalized = {};
            Object.entries(rawPlans).forEach(([dur, p]) => {
              normalized[dur] = {
                planId: p.planId ?? p.plan_id ?? '',
                price: p.price ?? 0,
                basePrice: p.basePrice ?? p.base_price ?? null,
                accessPrice: p.accessPrice ?? p.access_price ?? null,
                onePrice: p.onePrice ?? p.one_price ?? null,
                plusPrice: p.plusPrice ?? p.plus_price ?? null
              };
            });
            return normalized;
          })()
        },
        pharmacyName: product.pharmacyName || ''
      });
      setConfigTab(product.providerType || 'DrCare');
      setProductImages(product.images && product.images.length > 0 ? product.images : (product.image ? [{ url: product.image, isPrimary: true, id: "legacy" }] : []));

      // Load dosages for the product
      await loadProductDosages(product);

      // Load system promos for promo assignment
      await loadSystemPromos();
    } else {
      setEditingProduct(null);
      setProductForm({
        name: '',
        slug: '',
        shortDescription: '',
        category: categories[0]?.id || categories[0]?._id || '',
        isActive: true,
        isPrescriptionRequired: true,
        lifestyleImageUrl: '',
    isHidden: false,
    isComingSoon: false,
    isOutOfStock: false,
    isControlledSubstance: false,
    consultationFee: '',
    increasedStrengthFee: '',
    basePrice: '',
    retailPrice: '',
    shippingCost: '',
    medicationCost: '',
    consultationCost: '',
    miscCost: '',
    startingAtMode: 'auto',
    startingAtBasis: DEFAULT_STARTING_AT_BASIS,
    startingAtManual: { access: '', one: '', plus: '' },
    providerType: 'DrCare',
    doctorNowConfig: { encounterTypeId: '', consultType: 'Async', requiresAppointment: false, sku: '', productId: '', packageId: '', intakeFormId: '', plans: {} },
    pharmacyName: ''
      });
      setConfigTab('DrCare');
      setProductImages([]);
      setProductDosages([]);
      // A NEW product genuinely has no dosages and no promo assignment; make sure a previous
      // product's load failure does not carry into this modal.
      setDosagesError(null);
      setSystemPromosError(null);
    }
    console.log("[DEBUG] Opening product modal");
    setPromoConflictError(null);
    setProductModalOpen(true);
  };

  const handleProductSubmit = async (e) => {
    console.log("[DEBUG] handleProductSubmit called", { productForm, editingProduct });
    // Auto-set basePrice to Access tier starting-at for products with dosage pricing
    const autoAccessPrice = computeAutoStartingAt();

    // Prepare form data - convert dollar values to cents
    const formData = {
      ...productForm,
      syringeCode: productForm.syringeCode || null,
      syringeDescription: productForm.syringeDescription || null,
      consultationFee: productForm.consultationFee ? Math.round(parseFloat(productForm.consultationFee) * 100) : null,
      increasedStrengthFee: productForm.increasedStrengthFee ? Math.round(parseFloat(productForm.increasedStrengthFee) * 100) : null,
      shippingCost: productForm.shippingCost ? Math.round(parseFloat(productForm.shippingCost) * 100) : null,
      basePrice: (editingProduct && productDosages.length > 0 && autoAccessPrice) ? autoAccessPrice : (productForm.basePrice ? Math.round(parseFloat(productForm.basePrice) * 100) : null),
      retailPrice: productForm.retailPrice ? Math.round(parseFloat(productForm.retailPrice) * 100) : null,
      medicationCost: productForm.medicationCost ? Math.round(parseFloat(productForm.medicationCost) * 100) : null,
      consultationCost: productForm.consultationCost ? Math.round(parseFloat(productForm.consultationCost) * 100) : null,
      miscCost: productForm.miscCost ? Math.round(parseFloat(productForm.miscCost) * 100) : null,
      maxSupplyMonths: productForm.maxSupplyMonths ? parseInt(productForm.maxSupplyMonths) : 12,
      startingAtManual: manualFormToCents(productForm.startingAtManual)
    };
    // Only include doctorNowConfig when DoctorNow is active; omit for DrCare
    // so the backend does not overwrite stored DoctorNow config with null
    if (productForm.providerType === 'Doctor Now') {
      formData.doctorNowConfig = productForm.doctorNowConfig;
    } else {
      delete formData.doctorNowConfig;
    }
    // When the dosage load FAILED we do not know whether this product prices through its
    // dosages, so the basePrice guard above silently falls through to the form value and
    // would overwrite the stored dosage-derived price. Omit the key entirely instead --
    // exactly the defence the doctorNowConfig branch above uses: never overwrite what we
    // could not read.
    if (editingProduct && dosagesError) {
      delete formData.basePrice;
    }
    e.preventDefault();
    if (formData.increasedStrengthFee != null && (!Number.isFinite(formData.increasedStrengthFee) || formData.increasedStrengthFee < 0)) {
      toast.error('Increased Strength Fee must be a non-negative amount');
      return;
    }
    try {
      if (editingProduct) {
        await api.patch(`/admin/products/${editingProduct._id || editingProduct.id}`, formData);
        toast.success('Product updated successfully');
      } else {
        await api.post('/admin/products', formData);
        toast.success('Product created successfully');
      }
      // Warn about missing DN Plan IDs for DoctorNow products
      if (formData.providerType === 'Doctor Now' && productDosages.length > 0) {
        let missingPlanIds = false;
        productDosages.forEach(dosage => {
          const sp = dosage.supplyPricing || {};
          const spMeta = dosage.supplyPricingMeta || dosage.supply_pricing_meta || {};
          Object.keys(sp).forEach(months => {
            const durMeta = spMeta[months] || {};
            const pricing = sp[months];
            const hasPricing = pricing && typeof pricing === 'object' && (pricing.access != null || pricing.one != null || pricing.plus != null);
            const isDurEnabled = durMeta.enabled !== false && hasPricing;
            if (!isDurEnabled) return;
            if (!(durMeta.doctorNowPlanId ?? durMeta.doctor_now_plan_id)) missingPlanIds = true;
          });
        });
        if (missingPlanIds) {
          toast.warning('DoctorNow Plan ID is missing for one or more enabled supply durations.', { duration: 8000 });
        }
      }
      setPromoConflictError(null);
      setProductModalOpen(false);
      loadData();
    } catch (error) {
      if (error.status === 409) {
        setPromoConflictError(error.message);
      } else {
        toast.error(error.message || 'Failed to save product');
      }
    }
  };


  const handleReorderProduct = async (categoryId, productId, direction) => {
    const categoryProducts = products
      .filter(p => (p.category?.id || p.category?._id || p.categoryId) === categoryId)
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    
    const idx = categoryProducts.findIndex(p => (p._id || p.id) === productId);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= categoryProducts.length) return;

    const orders = categoryProducts.map((p, i) => ({
      id: p._id || p.id,
      display_order: i === idx ? swapIdx : i === swapIdx ? idx : i
    }));

    try {
      await api.post('/admin/products/reorder', { orders });
      // Update local state
      setProducts(prev => prev.map(p => {
        const order = orders.find(o => o.id === (p._id || p.id));
        return order ? { ...p, displayOrder: order.display_order } : p;
      }));
      toast.success('Products reordered');
    } catch (err) {
      toast.error('Failed to reorder products');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm('Are you sure you want to delete this product?')) return;
    try {
      await api.delete(`/admin/products/${productId}`);
      toast.success('Product deleted successfully');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete product');
    }
  };

  const handleToggleActive = async (productId, currentStatus) => {
    try {
      await api.patch(`/admin/products/${productId}`, { isActive: !currentStatus });
      toast.success(`Product ${currentStatus ? 'deactivated' : 'activated'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  // Category handlers
  const handleOpenCategoryModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name || '',
        slug: category.slug || '',
        shortDescription: category.shortDescription || '',
        icon: category.icon || '',
        image: category.image || null,
        isActive: category.isActive !== false,
        intakeFormId: category.intakeFormId || category.intakeForm?.id || ''
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({
        name: '',
        slug: '',
        shortDescription: '',
        icon: '',
        image: null,
        isActive: true,
        intakeFormId: ''
      });
    }
    setCategoryModalOpen(true);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await api.patch(`/admin/categories/${editingCategory._id || editingCategory.id}`, categoryForm);
        toast.success('Category updated successfully');
      } else {
        await api.post('/admin/categories', categoryForm);
        toast.success('Category created successfully');
      }
      setCategoryModalOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save category');
    }
  };

  
  const handleMoveCategory = async (categoryId, direction) => {
    const idx = categories.findIndex(c => (c._id || c.id) === categoryId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= categories.length) return;

    const orders = categories.map((c, i) => {
      const cId = c._id || c.id;
      let order = i;
      if (i === idx) order = swapIdx;
      if (i === swapIdx) order = idx;
      return { id: cId, displayOrder: order };
    });

    try {
      await api.post('/admin/categories/reorder', { orders });
      toast.success('Category order updated');
      loadData();
    } catch (error) {
      toast.error('Failed to reorder categories');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('Are you sure you want to delete this category?')) return;
    try {
      await api.delete(`/admin/categories/${categoryId}`);
      toast.success('Category deleted successfully');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete category');
    }
  };

  // Image upload handlers
  const handleProductImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !editingProduct) return;

    const productId = editingProduct._id || editingProduct.id;
    const formData = new FormData();

    for (let i = 0; i < files.length; i++) {
      formData.append('images', files[i]);
    }

    setUploadingImages(true);
    try {
      const response = await api.upload(`/admin/products/${productId}/images`, formData);
      setProductImages(response.images || []);
      toast.success('Images uploaded successfully');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to upload images');
    } finally {
      setUploadingImages(false);
      e.target.value = '';
    }
  };

  const handleLifestyleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !editingProduct) return;

    const productId = editingProduct._id || editingProduct.id;
    const formData = new FormData();
    formData.append("images", files[0]);

    setUploadingLifestyleImage(true);
    try {
      const response = await api.upload(`/admin/products/${productId}/lifestyle-image`, formData);
      setProductForm(prev => ({ ...prev, lifestyleImageUrl: response.lifestyleImageUrl }));
      toast.success("Lifestyle image uploaded");
      loadData();
    } catch (error) {
      toast.error(error.message || "Failed to upload lifestyle image");
    } finally {
      setUploadingLifestyleImage(false);
      e.target.value = "";
    }
  };

  const handleLifestyleImageDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0 || !editingProduct) return;
    const file = files[0];
    if (!file.type.startsWith('image/')) {
      toast.error('Please drop an image file');
      return;
    }

    const productId = editingProduct._id || editingProduct.id;
    const formData = new FormData();
    formData.append("images", file);

    setUploadingLifestyleImage(true);
    try {
      const response = await api.upload(`/admin/products/${productId}/lifestyle-image`, formData);
      setProductForm(prev => ({ ...prev, lifestyleImageUrl: response.lifestyleImageUrl }));
      toast.success("Lifestyle image uploaded");
      loadData();
    } catch (error) {
      toast.error(error.message || "Failed to upload lifestyle image");
    } finally {
      setUploadingLifestyleImage(false);
    }
  };

  const handleDeleteLifestyleImage = async () => {
    if (!editingProduct) return;
    const productId = editingProduct._id || editingProduct.id;

    try {
      await api.delete(`/admin/products/${productId}/lifestyle-image`);
      setProductForm(prev => ({ ...prev, lifestyleImageUrl: "" }));
      toast.success("Lifestyle image removed");
      loadData();
    } catch (error) {
      toast.error(error.message || "Failed to delete lifestyle image");
    }
  };


    const handleDeleteProductImage = async (imageId) => {
    if (!editingProduct) return;
    const productId = editingProduct._id || editingProduct.id;

    try {
      const response = await api.delete(`/admin/products/${productId}/images/${imageId}`);
      setProductImages(response.images || []);
      toast.success('Image deleted');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete image');
    }
  };

  const handleSetPrimaryImage = async (imageId) => {
    if (!editingProduct) return;
    const productId = editingProduct._id || editingProduct.id;

    try {
      const response = await api.patch(`/admin/products/${productId}/images/${imageId}/primary`);
      setProductImages(response.images || []);
      toast.success('Primary image updated');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to set primary image');
    }
  };

  const handleCategoryImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingCategory) return;

    const categoryId = editingCategory._id || editingCategory.id;
    const formData = new FormData();
    formData.append('image', file);

    setUploadingCategoryImage(true);
    try {
      const response = await api.upload(`/admin/categories/${categoryId}/image`, formData);
      setCategoryForm(prev => ({ ...prev, image: response.image }));
      toast.success('Category image uploaded');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to upload category image');
    } finally {
      setUploadingCategoryImage(false);
      e.target.value = '';
    }
  };

  const handleDeleteCategoryImage = async () => {
    if (!editingCategory) return;
    const categoryId = editingCategory._id || editingCategory.id;

    try {
      await api.delete(`/admin/categories/${categoryId}/image`);
      setCategoryForm(prev => ({ ...prev, image: null }));
      toast.success('Category image deleted');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete category image');
    }
  };

  // Dosage handlers
  const handleAddDosage = async () => {
    if (!editingProduct) return;
    // Belt-and-braces, mirroring handleClaimTicket in TicketDetailModal.jsx: without this every
    // click issued its own POST /dosages, so a double-click created two identical dosage rows.
    if (addDosageInFlight.current) return;
    addDosageInFlight.current = true;
    setAddingDosage(true);
    const productId = editingProduct._id || editingProduct.id;

    try {
      const newDosage = {
        amount: 'New Dosage',
        description: '',
        price: null,
        ndcCode: '',
        isActive: true,
        isDefault: productDosages.length === 0,
        displayOrder: productDosages.length
      };
      const response = await api.post(`/admin/products/${productId}/dosages`, newDosage);
      setProductDosages(prev => [...prev, response.dosage]);
      toast.success('Dosage added');
    } catch (error) {
      toast.error(error.message || 'Failed to add dosage');
    } finally {
      addDosageInFlight.current = false;
      setAddingDosage(false);
    }
  };

  // A dosage write changes the numbers the starting-at engine quotes, but NOT which basis is
  // selected — so the quote effect's `requested === saved` short-circuit leaves the Starting At
  // tier rows showing pre-edit prices until the modal is reopened. Re-ask the SAME endpoint that
  // effect uses, so the modal and the catalog stay one engine rather than two.
  //
  // Merges ONLY `startingAt`, deliberately: `handleOpenProductModal` is what populates
  // `productForm`, so a whole-product reload here would discard the operator's unsaved edits.
  // Mirrors the existing setEditingProduct(prev => ...) idiom used by the promo handlers below.
  //
  // A failure stays silent and keeps the last good prices on screen — the same rule the basis
  // preview already follows, because blanking a price reads as $0.
  const refreshStartingAt = async (productId) => {
    const basis = editingProduct?.startingAtBasis || DEFAULT_STARTING_AT_BASIS;
    try {
      const res = await api.get(`/admin/products/${productId}?startingAtBasis=${encodeURIComponent(basis)}`);
      const startingAt = res?.product?.startingAt;
      if (startingAt) setEditingProduct(prev => (prev ? { ...prev, startingAt } : prev));
    } catch {
      // keep the last good prices
    }
  };

  const handleUpdateDosage = async (dosageId, updates) => {
    if (!editingProduct) return;
    const productId = editingProduct._id || editingProduct.id;

    try {
      const response = await api.patch(`/admin/products/${productId}/dosages/${dosageId}`, updates);
      setProductDosages(prev =>
        prev.map(d => d.id === dosageId ? response.dosage : d)
      );
      refreshStartingAt(productId);
    } catch (error) {
      toast.error(error.message || 'Failed to update dosage');
    }
  };

  const handleDeleteDosage = async (dosageId) => {
    if (!editingProduct) return;
    const productId = editingProduct._id || editingProduct.id;

    try {
      await api.delete(`/admin/products/${productId}/dosages/${dosageId}`);
      setProductDosages(prev => prev.filter(d => d.id !== dosageId));
      toast.success('Dosage deleted');
    } catch (error) {
      toast.error(error.message || 'Failed to delete dosage');
    }
  };

  const handleSetDefaultDosage = async (dosageId) => {
    if (!editingProduct) return;
    const productId = editingProduct._id || editingProduct.id;

    try {
      await api.patch(`/admin/products/${productId}/dosages/${dosageId}/default`);
      setProductDosages(prev =>
        prev.map(d => ({ ...d, isDefault: d.id === dosageId }))
      );
      toast.success('Default dosage updated');
    } catch (error) {
      toast.error(error.message || 'Failed to set default dosage');
    }
  };

  // Promo assignment handlers
  const handleAssignPromo = async (promoId) => {
    const productId = editingProduct._id || editingProduct.id;
    try {
      await api.patch(`/admin/products/${productId}/promo`, { promoId });
      setEditingProduct(prev => ({ ...prev, productPromoId: promoId }));
      toast.success('Promo assigned');
    } catch (error) {
      toast.error(error.message || 'Failed to assign promo');
    }
  };

  const handleRemovePromo = async () => {
    const productId = editingProduct._id || editingProduct.id;
    try {
      await api.patch(`/admin/products/${productId}/promo`, { promoId: null });
      setEditingProduct(prev => ({ ...prev, productPromoId: null }));
      toast.success('Promo removed');
    } catch (error) {
      toast.error(error.message || 'Failed to remove promo');
    }
  };

  // Supply pricing helpers — labels from the shared supply-term map (key 1 = "60 Day")
  const SUPPLY_DURATIONS = [1, 3, 6, 12];
  const SUPPLY_LABELS = Object.fromEntries(SUPPLY_DURATIONS.map(m => [m, getSupplyLabel(m)]));
  const DN_PLAN_DURATIONS = [1, 3, 6, 12];

  // Helper to get the price for a specific tier from tier-aware supply_pricing
  const getTierPrice = (supplyPricing, months, tier) => {
    const entry = supplyPricing?.[String(months)];
    if (!entry || typeof entry !== 'object') return null;
    return entry[tier] ?? null;
  };

  const getSupplyMeta = (dosage, months) => {
    const meta = dosage?.supplyPricingMeta || {};
    return meta[String(months)] || {};
  };

  // Price modifier for the tier the Supply Duration Pricing grid is currently editing.
  // customTiers is loaded on mount (loadCustomTiers, :506) and is the ONLY table carrying
  // product_price_modifier — membership_tiers has no such column. `null` when the selected
  // tier carries none, which is what suppresses the indicator. Task 17.
  const tierPriceModifiers = buildTierModifierMap(customTiers);
  const activeTierModifier = tierPriceModifiers[supplyPricingTier] ?? null;
  const hasPriceModifier = activeTierModifier !== null && activeTierModifier.modifier !== 0;

  const handleSupplyPricingChange = (dosageId, currentPricing, months, tier, newCents) => {
    const updated = { ...(currentPricing || {}) };
    const monthKey = String(months);
    const currentTiers = updated[monthKey] || {};
    if (newCents === null || newCents === undefined || isNaN(newCents)) {
      const newTiers = { ...currentTiers };
      delete newTiers[tier];
      if (Object.keys(newTiers).length === 0) {
        delete updated[monthKey];
      } else {
        updated[monthKey] = newTiers;
      }
    } else {
      updated[monthKey] = { ...currentTiers, [tier]: newCents };
    }
    handleUpdateDosage(dosageId, { supplyPricing: Object.keys(updated).length > 0 ? updated : null });
  };

  const handleSupplyMetaChange = (dosageId, currentMeta, months, field, value) => {
    const updated = { ...(currentMeta || {}) };
    const key = String(months);
    updated[key] = { ...(updated[key] || {}), [field]: value };
    // If setting isDefault, clear isDefault on others
    if (field === 'isDefault' && value) {
      for (const k of Object.keys(updated)) {
        if (k !== key) updated[k] = { ...updated[k], isDefault: false };
      }
    }
    handleUpdateDosage(dosageId, { supplyPricingMeta: updated });
  };

  const handleDnProductIdChange = (dosageId, currentMeta, months, value) => {
    const updated = { ...(currentMeta || {}) };
    const key = String(months);
    const existing = updated[key] || {};
    updated[key] = {
      ...existing,
      doctorNowProductId: value
    };
    handleUpdateDosage(dosageId, { supplyPricingMeta: updated });
  };

  const handleDnPlanIdChange = (dosageId, currentMeta, months, value) => {
    const updated = { ...(currentMeta || {}) };
    const key = String(months);
    const existing = updated[key] || {};
    updated[key] = {
      ...existing,
      doctorNowPlanId: value
    };
    handleUpdateDosage(dosageId, { supplyPricingMeta: updated });
  };

  const handleDnOurCostChange = (dosageId, currentMeta, months, value) => {
    const updated = { ...(currentMeta || {}) };
    const key = String(months);
    const existing = updated[key] || {};
    updated[key] = {
      ...existing,
      ourCost: value
    };
    handleUpdateDosage(dosageId, { supplyPricingMeta: updated });
  };

  const handleDnConsultationCostChange = (dosageId, currentMeta, months, value) => {
    const updated = { ...(currentMeta || {}) };
    const key = String(months);
    const existing = updated[key] || {};
    updated[key] = {
      ...existing,
      consultationCost: value
    };
    handleUpdateDosage(dosageId, { supplyPricingMeta: updated });
  };

  const handleDnShippingCostChange = (dosageId, currentMeta, months, value) => {
    const updated = { ...(currentMeta || {}) };
    const key = String(months);
    const existing = updated[key] || {};
    updated[key] = {
      ...existing,
      shippingAndHandling: value
    };
    handleUpdateDosage(dosageId, { supplyPricingMeta: updated });
  };

    const handleAddDnPlan = (dosageId, currentPricing, currentMeta) => {
    const usedMonths = Object.keys(currentPricing || {}).map(Number);
    const available = DN_PLAN_DURATIONS.filter(m => !usedMonths.includes(m));
    if (available.length === 0) return;
    const months = available[0];
    const key = String(months);
    const updatedPricing = { ...(currentPricing || {}), [key]: { access: null, one: null, plus: null } };
    const existingDurMeta = (currentMeta || {})[key] || {};
    const updatedMeta = { ...(currentMeta || {}), [key]: { ...existingDurMeta, enabled: true } };
    handleUpdateDosage(dosageId, { supplyPricing: updatedPricing, supplyPricingMeta: updatedMeta });
  };

  const handleRemoveDnPlan = (dosageId, currentPricing, currentMeta, months) => {
    const key = String(months);
    const { [key]: _p, ...restPricing } = currentPricing || {};
    const { [key]: _m, ...restMeta } = currentMeta || {};
    handleUpdateDosage(dosageId, {
      supplyPricing: Object.keys(restPricing).length > 0 ? restPricing : null,
      supplyPricingMeta: restMeta
    });
  };

  const handleChangeDnPlanMonths = (dosageId, currentPricing, currentMeta, oldMonths, newMonths) => {
    const oldKey = String(oldMonths);
    const newKey = String(newMonths);
    if (oldKey === newKey) return;
    const { [oldKey]: pricingData, ...restPricing } = currentPricing || {};
    const { [oldKey]: metaData, ...restMeta } = currentMeta || {};
    const updatedPricing = { ...restPricing, [newKey]: pricingData || { access: null, one: null, plus: null } };
    const updatedMeta = { ...restMeta, [newKey]: metaData || { enabled: true } };
    handleUpdateDosage(dosageId, { supplyPricing: updatedPricing, supplyPricingMeta: updatedMeta });
  };

  const handleInitSupplyPricing = (dosageId, basePrice) => {
    const base = basePrice || 0;
    const m3 = Math.round(base * 0.85);
    const m6 = Math.round(base * 0.75);
    const m12 = Math.round(base * 0.65);
    const pricing = {
      '1': { access: base, one: base, plus: base },
      '3': { access: m3, one: m3, plus: m3 },
      '6': { access: m6, one: m6, plus: m6 },
      '12': { access: m12, one: m12, plus: m12 }
    };
    const meta = {
      '1': { enabled: true, isDefault: false },
      '3': { enabled: true, isDefault: true },
      '6': { enabled: true, isDefault: false },
      '12': { enabled: false, isDefault: false }
    };
    handleUpdateDosage(dosageId, { supplyPricing: pricing, supplyPricingMeta: meta });
  };

  // Auto-compute "Oval Price" for a dosage: Access tier price for the default supply duration
  const computeOvalPrice = (dosage) => {
    const sp = dosage?.supplyPricing;
    const meta = dosage?.supplyPricingMeta || {};
    if (!sp || typeof sp !== 'object' || Object.keys(sp).length === 0) return dosage?.price || null;
    // Find default duration
    const defaultEntry = Object.entries(meta).find(([, v]) => v?.isDefault);
    if (defaultEntry) {
      const tierPricing = sp[defaultEntry[0]];
      const accessPrice = typeof tierPricing === 'object' ? tierPricing?.access : tierPricing;
      if (accessPrice && accessPrice > 0) return accessPrice;
    }
    // No default set — use 1-month access price
    const oneMonth = sp['1'];
    return (typeof oneMonth === 'object' ? oneMonth?.access : oneMonth) || dosage?.price || null;
  };

  // A product prices EITHER through its dosages (Starting At, read-only) or through its own
  // base price. One predicate for both the label and the control so they cannot drift apart.
  const hasDosagePricing = Boolean(editingProduct) && productDosages.length > 0;

  // Auto-compute "Starting At" prices per tier: cheapest monthly rate across all active dosages
  const computeTierStartingAt = () => {
    if (!productDosages || productDosages.length === 0) return null;
    const result = { access: Infinity, one: Infinity, plus: Infinity };
    const tierKeys = ['access', 'one', 'plus'];
    let hasAnyPricing = false;
    for (const d of productDosages) {
      if (d.isActive === false) continue;
      const sp = d.supplyPricing;
      const meta = d.supplyPricingMeta || {};
      if (sp && typeof sp === 'object') {
        for (const [moStr, tierPricing] of Object.entries(sp)) {
          if (!tierPricing || typeof tierPricing !== 'object') continue;
          if (meta[moStr]?.enabled === false) continue;
          for (const tier of tierKeys) {
            const cents = tierPricing[tier];
            if (cents && cents > 0 && cents < result[tier]) {
              result[tier] = cents;
              hasAnyPricing = true;
            }
          }
        }
      } else if (d.price && d.price > 0) {
        for (const tier of tierKeys) {
          if (d.price < result[tier]) {
            result[tier] = d.price;
            hasAnyPricing = true;
          }
        }
      }
    }
    if (!hasAnyPricing) return null;
    return {
      access: result.access === Infinity ? null : result.access,
      one: result.one === Infinity ? null : result.one,
      plus: result.plus === Infinity ? null : result.plus
    };
  };
  // Backward compat wrapper
  const computeAutoStartingAt = () => {
    const tierPrices = computeTierStartingAt();
    return tierPrices?.access ?? null;
  };

  // Product Offer handlers
  const handleOpenProductOfferModal = (product) => {
    setEditingProductForOffer(product);
    setProductOfferModalOpen(true);
  };

  const handleCloseProductOfferModal = () => {
    setProductOfferModalOpen(false);
    setEditingProductForOffer(null);
  };

  const handleProductOfferSave = () => {
    handleCloseProductOfferModal();
    loadData(); // Refresh data to update offer status
  };

  // Intake Form handlers
  const handleOpenIntakeModal = (product) => {
    setEditingIntakeProduct(product);
    setIntakeModalOpen(true);
  };

  const handleCloseIntakeModal = () => {
    setIntakeModalOpen(false);
    setEditingIntakeProduct(null);
  };

  const handleIntakeFormSave = () => {
    handleCloseIntakeModal();
    loadData(); // Refresh data to update intake form status badges
  };

  // Info Popup handlers
  const handleOpenInfoPopup = (product) => {
    setInfoPopupProduct(product);
    setInfoPopupForm({
      detailedDescription: product.detailedDescription || '',
      lifestyleImageUrl: product.lifestyleImageUrl || '',
        isHidden: product.isHidden === true,
        isComingSoon: product.isComingSoon === true,
        consultationFee: product.consultationFee != null ? (product.consultationFee / 100).toFixed(2) : '',
        basePrice: product.basePrice != null ? (product.basePrice / 100).toFixed(2) : '',
        retailPrice: product.retailPrice != null ? (product.retailPrice / 100).toFixed(2) : '',
        shippingCost: product.shippingCost != null ? (product.shippingCost / 100).toFixed(2) : '',
        drcareSpecialtyId: product.drcareSpecialtyId || '',
        drcareReasonId: product.drcareReasonId || ''
    });
    setInfoPopupModalOpen(true);
  };

  const handleSaveInfoPopup = async () => {
    if (!infoPopupProduct) return;
    const productId = infoPopupProduct._id || infoPopupProduct.id;

    setSavingInfoPopup(true);
    try {
      // Save detailed description
      await api.put(`/admin/products/${productId}/detailed-description`, {
        detailedDescription: infoPopupForm.detailedDescription || null
      });

      // Save lifestyle image URL via product update
      await api.patch(`/admin/products/${productId}`, {
        lifestyleImageUrl: infoPopupForm.lifestyleImageUrl || null
      });

      toast.success('Info popup content saved');
      setInfoPopupModalOpen(false);
      setInfoPopupProduct(null);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save info popup content');
    } finally {
      setSavingInfoPopup(false);
    }
  };

  // Helper to format cents to dollars for display
  const formatDollars = (cents) => {
    if (cents === null || cents === undefined) return '';
    return (cents / 100).toFixed(2);
  };

  // Helper to parse dollars to cents
  const parseCents = (dollars) => {
    if (!dollars && dollars !== 0) return null;
    return Math.round(parseFloat(dollars) * 100);
  };

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>Products & Categories</h1>
          <p>Manage your product catalog</p>
        </div>
        <div className={styles.headerActions}>
          {canCreateProducts && <Button variant="outline" onClick={() => handleOpenCategoryModal()}>Add Category</Button>}
          {canCreateProducts && <Button onClick={() => handleOpenProductModal()}>Add Product</Button>}
        </div>
      </div>

      {/* Catalog Behavior Settings */}
      <div className={styles.catalogSettingsBanner}>
        <div className={styles.catalogSettingsInfo}>
          <span className={styles.catalogSettingsLabel}>Category Click Goes to View All</span>
          <span className={styles.catalogSettingsDesc}>
            When enabled, clicking Get Started on a category card in the catalog navigates to the category View All page instead of a specific product.
          </span>
        </div>
        <button
          className={`${styles.toggle} ${categoryClickGoesToViewAll ? styles.toggleActive : ''}`}
          onClick={handleToggleCatalogSetting}
          disabled={savingCatalogSetting}
          role="switch"
          aria-checked={categoryClickGoesToViewAll}
          aria-label="Category Click Goes to View All"
        >
          <div className={styles.toggleSlider} />
        </button>
      </div>

            {/* Products by Category - Spreadsheet Style */}
      {/* Membership Tiers Section */}
      <section className={styles.section}>
        <div className={styles.spreadsheetCard}>
          <div className={styles.spreadsheetHeader}>
            <div className={styles.spreadsheetIcon}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <span className={styles.spreadsheetTitle}>Membership Tiers</span>
            <span className={styles.catalogCount}>{tiers.length} tiers</span>
            {!tiersError && tiers.length < 4 && (
              <button
                className={styles.headerAddBtn}
                onClick={() => handleOpenTierModal()}
                style={{ marginLeft: 'auto' }}
              >
                + Add Tier
              </button>
            )}
          </div>

          <div className={styles.tableBody}>
            {loadingTiers ? (
              <div className={styles.empty}><LoadingSpinner /></div>
            ) : tiersError ? (
              <ListLoadError message={tiersError} onRetry={loadTiers} />
            ) : tiers.length === 0 ? (
              <div className={styles.empty}>No membership tiers found.</div>
            ) : (
              <>
                <div className={styles.tierHeader}>
                  <div className={styles.tierColName}>Tier Name</div>
                  <div className={styles.tierColPrice}>Price</div>
                  <div className={styles.tierColFeatures}>Features</div>
                  <div className={styles.tierColStatus}>Status</div>
                  <div className={styles.tierColActions}>Actions</div>
                </div>
                {tiers.map((tier) => (
                  <div key={tier.id} className={styles.tierRow}>
                    <div className={styles.tierColName}>
                      <span className={styles.tierName}>{tier.name}</span>
                      {tier.description && (
                        <span className={styles.tierDesc}>{tier.description}</span>
                      )}
                    </div>
                    <div className={styles.tierColPrice}>
                      <span className={styles.tierPrice}>
                        {tier.price === 0 ? 'Free' : `$${(tier.price / 100).toFixed(2)}`}
                      </span>
                      {tier.price > 0 && <span className={styles.tierPriceUnit}>/month</span>}
                    </div>
                    <div className={styles.tierColFeatures}>
                      <div className={styles.featurePills}>
                        {(Array.isArray(tier.features) ? tier.features : []).filter(f => f.isIncluded).map((f, i) => (
                          <span key={i} className={styles.featurePill}>{f.featureName}</span>
                        ))}
                        {(Array.isArray(tier.features) ? tier.features : []).filter(f => !f.isIncluded).length > 0 && (
                          <span className={`${styles.featurePill} ${styles.featureExcluded}`}>
                            +{(Array.isArray(tier.features) ? tier.features : []).filter(f => !f.isIncluded).length} excluded
                          </span>
                        )}
                      </div>
                    </div>
                    <div className={styles.tierColStatus}>
                      <span className={`${styles.statusBadge} ${tier.isActive ? styles.active : styles.inactive}`}>
                        {tier.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className={styles.tierColActions}>
                      <button className={styles.actionBtn} onClick={() => handleOpenTierModal(tier)}>Edit</button>
                      {tiers.length > 3 && (
                        <button className={`${styles.actionBtn} ${styles.danger}`} onClick={() => handleDeleteTier(tier.id)}>Remove</button>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Custom Membership Tiers Section */}
      <section className={styles.section}>
        <div className={styles.spreadsheetCard}>
          <div className={styles.spreadsheetHeader}>
            <div className={styles.spreadsheetIcon}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <span className={styles.spreadsheetTitle}>Custom Membership Tiers</span>
            <span className={styles.catalogCount}>{customTiers.length} custom tiers</span>
            <button
              className={styles.headerAddBtn}
              onClick={() => handleOpenCustomTierModal()}
              style={{ marginLeft: 'auto' }}
            >
              + Add Custom Tier
            </button>
          </div>

          <div className={styles.tableBody}>
            {loadingCustomTiers ? (
              <div className={styles.empty}><LoadingSpinner /></div>
            ) : customTiersError ? (
              <ListLoadError message={customTiersError} onRetry={loadCustomTiers} />
            ) : customTiers.length === 0 ? (
              <div className={styles.empty}>No custom membership tiers yet. Create one to assign to specific clients.</div>
            ) : (
              <>
                <div className={styles.tierHeader}>
                  <div className={styles.tierColName}>Tier Name</div>
                  <div className={styles.tierColPrice}>Price</div>
                  <div className={styles.tierColFeatures}>Modifiers</div>
                  <div className={styles.tierColStatus}>Status</div>
                  <div className={styles.tierColActions}>Actions</div>
                </div>
                {customTiers.map((tier) => (
                  <div key={tier.id} className={styles.tierRow}>
                    <div className={styles.tierColName}>
                      <span className={styles.tierName}>{tier.name}</span>
                      <span style={{fontSize:'0.7rem',color:'#7c3aed',fontWeight:600,marginLeft:4}}>CUSTOM</span>
                    </div>
                    <div className={styles.tierColPrice}>
                      <span className={styles.tierPrice}>
                        {tier.monthlyPrice === 0 ? 'Free' : `$${(tier.monthlyPrice / 100).toFixed(2)}`}
                      </span>
                      {tier.monthlyPrice > 0 && <span className={styles.tierPriceUnit}>/month</span>}
                    </div>
                    <div className={styles.tierColFeatures}>
                      <div className={styles.featurePills}>
                        {!tier.consultationFeeIncluded && (
                          <span className={`${styles.featurePill} ${styles.featureExcluded}`}>No Consult Fee</span>
                        )}
                        {!tier.shippingHandlingIncluded && (
                          <span className={`${styles.featurePill} ${styles.featureExcluded}`}>No Shipping</span>
                        )}
                        {tier.productPriceModifier !== 0 && (
                          <span className={styles.featurePill}>
                            {tier.productPriceModifier > 0 ? '+' : ''}{(tier.productPriceModifier * 100).toFixed(0)}% price
                          </span>
                        )}
                        {(Array.isArray(tier.features) ? tier.features : []).filter(f => f.isIncluded !== false).length > 0 && (
                          <span className={styles.featurePill}>
                            {(Array.isArray(tier.features) ? tier.features : []).filter(f => f.isIncluded !== false).length} features
                          </span>
                        )}
                      </div>
                    </div>
                    <div className={styles.tierColStatus}>
                      <span className={`${styles.statusBadge} ${tier.isActive ? styles.active : styles.inactive}`}>
                        {tier.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className={styles.tierColActions}>
                      <button className={styles.actionBtn} onClick={() => handleOpenCustomTierModal(tier)}>Edit</button>
                      <button className={`${styles.actionBtn} ${styles.danger}`} onClick={() => handleDeleteCustomTier(tier.id)}>Delete</button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.spreadsheetCard}>

      {/* Syringes Section */}
      <div style={{ marginBottom: '24px' }}>
          <div className={styles.spreadsheetHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span className={styles.spreadsheetTitle}>Syringes</span>
              <span className={styles.catalogCount}>{syringes.length} syringes</span>
            </div>
            {canCreateProducts && <button className={styles.actionBtn} style={{ background: '#e67e22', color: 'white', border: 'none', padding: '6px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }} onClick={() => handleOpenSyringeModal()}>+ Add Syringe</button>}
          </div>
          <div className={styles.tableBody}>
            {loadingSyringes ? (
              <div style={{ padding: '16px', textAlign: 'center', color: '#8a7e6b' }}>Loading syringes...</div>
            ) : syringesError ? (
              <ListLoadError message={syringesError} onRetry={loadSyringes} />
            ) : syringes.length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', color: '#8a7e6b' }}>No syringes added yet</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e8e0d0', textAlign: 'left' }}>
                    <th style={{ padding: '10px 12px', fontWeight: 600, color: '#5a4e3c' }}>Code</th>
                    <th style={{ padding: '10px 12px', fontWeight: 600, color: '#5a4e3c' }}>Description</th>
                    <th style={{ padding: '10px 12px', fontWeight: 600, color: '#5a4e3c' }}>Fulfillment Qty</th>
                    <th style={{ padding: '10px 12px', fontWeight: 600, color: '#5a4e3c' }}>Status</th>
                    <th style={{ padding: '10px 12px', fontWeight: 600, color: '#5a4e3c' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {syringes.map(syringe => (
                    <tr key={syringe.id} style={{ borderBottom: '1px solid #f0e8d8' }}>
                      <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontWeight: 600 }}>{syringe.code}</td>
                      <td style={{ padding: '10px 12px' }}>{syringe.description}</td>
                      <td style={{ padding: '10px 12px' }}>{syringe.fulfillmentQuantity}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 500, background: syringe.isActive ? '#dcfce7' : '#fee2e2', color: syringe.isActive ? '#16a34a' : '#dc2626' }}>
                          {syringe.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {canEditProducts && <button className={styles.actionBtn} onClick={() => handleOpenSyringeModal(syringe)}>Edit</button>}
                          <button className={`${styles.actionBtn} ${styles.danger}`} onClick={() => handleDeleteSyringe(syringe.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
      </div>

      {/* Syringe Modal */}
      {syringeModalOpen && (
        <Modal isOpen={syringeModalOpen} onClose={() => setSyringeModalOpen(false)} title={editingSyringe ? "Edit Syringe" : "Add Syringe"}>
          <form onSubmit={handleSyringeSubmit}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Code</label>
              <Input value={syringeForm.code} onChange={(e) => setSyringeForm(prev => ({ ...prev, code: e.target.value }))} placeholder="e.g. PHU00035" required />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Description</label>
              <Input value={syringeForm.description} onChange={(e) => setSyringeForm(prev => ({ ...prev, description: e.target.value }))} placeholder="e.g. 1ml Insulin Syringe, 31G x 5/16 inch" required />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Fulfillment Quantity</label>
              <Input type="number" step="0.1" min="0.1" value={syringeForm.fulfillmentQuantity} onChange={(e) => setSyringeForm(prev => ({ ...prev, fulfillmentQuantity: parseFloat(e.target.value) || 1 }))} placeholder="1" />
              <div style={{ fontSize: '12px', color: '#8a7e6b', marginTop: '4px' }}>Quantity sent with fulfillment orders. Default is 1.</div>
            </div>
            <div className={styles.formGroup}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" checked={syringeForm.isActive} onChange={(e) => setSyringeForm(prev => ({ ...prev, isActive: e.target.checked }))} />
                Active
              </label>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <Button type="button" variant="outline" onClick={() => setSyringeModalOpen(false)}>Cancel</Button>
              <Button type="submit">{editingSyringe ? 'Update' : 'Create'}</Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Product Catalog Section */}
          <div className={styles.spreadsheetHeader}>
            <div className={styles.spreadsheetIcon}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                <line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
            </div>
            <span className={styles.spreadsheetTitle}>Product Catalog</span>
            <span className={styles.catalogCount}>{categories.length} categories • {products.length} products</span>
          </div>

          <div className={styles.tableBody}>
            {categories.map((category) => {
              const categoryId = category._id || category.id;
              const categoryProducts = products.filter(
                (p) => (p.category?.id || p.category?._id || p.categoryId) === categoryId
              ).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

              return (
                <div key={categoryId} className={styles.categoryGroup}>
                  {/* Category Row */}
                  <div className={styles.categoryRow}>
                    <div className={styles.categoryRowLeft}>
                      <div className={styles.categoryRowIcon}>
                        {category.image ? (
                          <img loading="lazy" src={category.image} alt={category.name} />
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                          </svg>
                        )}
                      </div>
                      <div className={styles.categoryRowInfo}>
                        <span className={styles.categoryRowName}>{category.name}</span>
                        <span className={styles.categoryRowMeta}>
                          {categoryProducts.length} product{categoryProducts.length !== 1 ? 's' : ''}
                          {category.shortDescription && ` • ${category.shortDescription}`}
                        </span>
                      </div>
                    </div>
                    <div className={styles.categoryRowActions}>
                      <div className={styles.reorderBtns}>
                        <button
                          type="button"
                          className={styles.reorderBtn}
                          onClick={() => handleMoveCategory(categoryId, 'up')}
                          disabled={categories.indexOf(category) === 0}
                          title="Move up"
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          className={styles.reorderBtn}
                          onClick={() => handleMoveCategory(categoryId, 'down')}
                          disabled={categories.indexOf(category) === categories.length - 1}
                          title="Move down"
                        >
                          ▼
                        </button>
                      </div>
                      <span className={`${styles.statusBadge} ${category.isActive ? styles.active : styles.inactive}`}>
                        {category.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {canEditProducts && (
                        <button
                          className={`${styles.actionBtn} ${styles.editBtn}`}
                          onClick={() => handleOpenCategoryModal(category)}
                        >
                          Edit
                        </button>
                      )}
                      <button
                        className={`${styles.actionBtn} ${styles.danger}`}
                        onClick={() => handleDeleteCategory(categoryId)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  {/* Product Rows under Category */}
                  {categoryProducts.length > 0 && (
                    <div className={styles.productRows}>
                      <div className={styles.productRowHeader}>
                        <span className={styles.colPriority}></span>
                        <span className={styles.colProduct}>Product</span>
                        <span className={styles.colIntake}>Intake Form</span>
                        <span className={styles.colTierPrice}>Access ($)</span>
                        <span className={styles.colTierPrice}>ONE ($)</span>
                        <span className={styles.colTierPrice}>PLUS ($)</span>
                        <span className={styles.colCost}>Cost</span>
                        <span className={styles.colStatus}>Status</span>
                        <span className={styles.colProvider}>Active Provider</span>
                        <span className={styles.colProvider}>All Providers</span>
                        <span className={styles.colOrders}>Orders</span>
                        <span className={styles.colActions}>Actions</span>
                      </div>
                      {categoryProducts.map((product) => (
                        <div key={product._id || product.id} className={`${styles.productRow} ${(product.isActive === false || product.isHidden || product.isComingSoon || product.isOutOfStock) ? styles.productRowInactive : ''}`}>
                          <div className={styles.colPriority}>
                            <div className={styles.reorderBtns}>
                              <button
                                type="button"
                                className={styles.reorderBtn}
                                onClick={() => handleReorderProduct(categoryId, product._id || product.id, 'up')}
                                title="Move up"
                                aria-label="Move product up"
                              >&#9650;</button>
                              <button
                                type="button"
                                className={styles.reorderBtn}
                                onClick={() => handleReorderProduct(categoryId, product._id || product.id, 'down')}
                                title="Move down"
                                aria-label="Move product down"
                              >&#9660;</button>
                            </div>
                          </div>
                          <div className={styles.colProduct}>
                            <div className={styles.productInfo}>
                              <div className={styles.productImage}>
                                {product.primaryImage ? (
                                  <img loading="lazy" src={product.primaryImage} alt={product.name} />
                                ) : (
                                  <span>💊</span>
                                )}
                              </div>
                              <div>
                                <div className={styles.productName}>
                                  {product.name}
                                  {product.syringeCode && (
                                    <span className={styles.syringeBadge} title={product.syringeDescription || 'Syringe included'}>
                                      Syringe
                                    </span>
                                  )}
                                  {(() => {
                                    const status = product.isActive === false ? 'paused' : product.isHidden ? 'hidden' : product.isComingSoon ? 'coming_soon' : product.isOutOfStock ? 'out_of_stock' : 'active';
                                    const cfg = { active: { label: 'Active', cls: 'statusBadgeActive' }, paused: { label: 'Paused', cls: 'statusBadgePaused' }, coming_soon: { label: 'Coming Soon', cls: 'statusBadgeComingSoon' }, out_of_stock: { label: 'Out of Stock', cls: 'statusBadgeOutOfStock' }, hidden: { label: 'Hidden', cls: 'statusBadgeHidden' } };
                                    const { label, cls } = cfg[status];
                                    return (
                                      <span className={styles[cls]} title={label}>
                                        {label}
                                      </span>
                                    );
                                  })()}
                                  {/* NDC Missing Badge */}
                                  {(() => {
                                    const missingNdc = getMissingNdcDosages(product.dosages);
                                    if (!product.dosages || product.dosages.length === 0) {
                                      return (
                                        <span className={styles.noDosagesBadge} title="No dosage options configured">
                                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10" />
                                            <line x1="12" y1="8" x2="12" y2="12" />
                                            <line x1="12" y1="16" x2="12.01" y2="16" />
                                          </svg>
                                          No Dosages
                                        </span>
                                      );
                                    }
                                    if (missingNdc.length > 0) {
                                      const missingList = missingNdc.map(d => d.amount || d.description || 'Unnamed').join(', ');
                                      return (
                                        <span 
                                          className={styles.ndcMissingBadge} 
                                          title={`Missing NDC: ${missingList}`}
                                        >
                                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                            <line x1="12" y1="9" x2="12" y2="13" />
                                            <line x1="12" y1="17" x2="12.01" y2="17" />
                                          </svg>
                                          NDC Missing
                                        </span>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>
                                <div className={styles.productSlug}>{product.slug}</div>
                                {product.dosages && product.dosages.length > 0 && (
                                  <div className={styles.productDosages}>
                                    {product.dosages.map(d => (
                                      <span key={d.id} className={styles.dosagePill}>
                                        {d.amount}{d.price ? ` - $${(d.price / 100).toFixed(0)}` : ""}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <span className={styles.colIntake}>
                            {product.hasIntakeForm ? (
                              <span
                                className={`${styles.intakeBadge} ${product.intakeFormComplete ? styles.intakeComplete : styles.intakeIncomplete}`}
                                title={product.intakeFormComplete
                                  ? `${product.intakeFormFieldCount} fields configured`
                                  : 'No fields configured'}
                              >
                                {product.intakeFormComplete ? (
                                  <>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                    Complete
                                  </>
                                ) : (
                                  <>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <circle cx="12" cy="12" r="10" />
                                      <line x1="12" y1="8" x2="12" y2="12" />
                                      <line x1="12" y1="16" x2="12.01" y2="16" />
                                    </svg>
                                    Incomplete
                                  </>
                                )}
                              </span>
                            ) : (
                              <span className={`${styles.intakeBadge} ${styles.intakeMissing}`} title="No intake form attached">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10" />
                                  <line x1="15" y1="9" x2="9" y2="15" />
                                  <line x1="9" y1="9" x2="15" y2="15" />
                                </svg>
                                Missing
                              </span>
                            )}
                          </span>
                          {STARTING_AT_TIERS.map(({ key }) => (
                            <span key={key} className={styles.colTierPrice}>
                              {formatTierPrice(getStartingAtTierPrices(product)[key])}
                            </span>
                          ))}
                          <span className={styles.colCost}>
                            {product.medicationCost ? "$" + (product.medicationCost / 100).toFixed(2) : "—"}
                          </span>
                          <span className={styles.colStatus}>
                            <span className={`${styles.status} ${product.isActive ? styles.active : styles.inactive}`}>
                              {product.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </span>
                          <span className={styles.colProvider}>
                            <span className={styles.providerBadges}>
                              {(() => {
                                const providerType = product.provider_type ?? product.providerType ?? 'DrCare';
                                const isDoctorNow = providerType === 'Doctor Now';
                                return (
                                  <span
                                    className={`${styles.providerBadge} ${isDoctorNow ? styles.providerBadgeDoctorNow : styles.providerBadgeDrCare}`}
                                  >
                                    {isDoctorNow ? 'DN' : 'DC'}
                                  </span>
                                );
                              })()}
                            </span>
                          </span>
                          <span className={styles.colProvider}>
                            <span className={styles.providerBadges}>
                              {(() => {
                                const hasDrCare = !!(product.drcare_specialty_id ?? product.drcareSpecialtyId);
                                const hasDoctorNow = !!(product.doctor_now_config ?? product.doctorNowConfig);
                                if (hasDrCare && hasDoctorNow) {
                                  return (
                                    <>
                                      <span className={`${styles.providerBadge} ${styles.providerBadgeDrCare}`}>DC</span>
                                      <span className={`${styles.providerBadge} ${styles.providerBadgeDoctorNow}`}>DN</span>
                                    </>
                                  );
                                }
                                if (hasDrCare) return <span className={`${styles.providerBadge} ${styles.providerBadgeDrCare}`}>DC</span>;
                                if (hasDoctorNow) return <span className={`${styles.providerBadge} ${styles.providerBadgeDoctorNow}`}>DN</span>;
                                return <span style={{ color: "#999" }}>—</span>;
                              })()}
                            </span>
                          </span>
                          <span className={styles.colOrders}>{product.stats?.orderCount || 0}</span>
                          <div className={styles.colActions}>

                            {canEditProducts && (
                              <button
                                className={`${styles.iconBtn} ${styles.editBtn}`}
                                onClick={() => handleOpenProductModal(product)}
                                title="Edit Product"
                                aria-label="Edit Product"
                                >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                              </button>
                            )}
                            <button
                              className={`${styles.iconBtn} ${styles.intakeBtn}`}
                              onClick={() => handleOpenIntakeModal(product)}
                              title="Edit Intake"
                              aria-label="Edit Intake"
                              >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                              </svg>
                            </button>
                            <button
                              className={`${styles.iconBtn} ${styles.infoBtn}`}
                              onClick={() => handleOpenInfoPopup(product)}
                              title="Edit Info"
                              aria-label="Edit Info"
                              >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="16" x2="12" y2="12" />
                                <line x1="12" y1="8" x2="12.01" y2="8" />
                              </svg>
                            </button>
                            <button
                              className={`${styles.iconBtn} ${styles.offerBtn} ${productOffers[product._id || product.id] ? styles.offerActive : ""}`}
                              onClick={() => handleOpenProductOfferModal(product)}
                              title={productOffers[product._id || product.id] ? "Edit Offer" : "Create Offer"}
                              aria-label={productOffers[product._id || product.id] ? "Edit Offer" : "Create Offer"}
                              >
                              {productOffers[product._id || product.id] ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                </svg>
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                </svg>
                              )}
                            </button>
                            <button
                              className={`${styles.iconBtn} ${product.isActive ? styles.deactivateBtn : styles.activateBtn}`}
                              onClick={() => handleToggleActive(product._id || product.id, product.isActive)}
                              title={product.isActive ? "Deactivate Product" : "Activate Product"}
                              aria-label={product.isActive ? "Deactivate Product" : "Activate Product"}
                              >
                              {product.isActive ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="6" y="4" width="4" height="16" />
                                  <rect x="14" y="4" width="4" height="16" />
                                </svg>
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polygon points="5 3 19 12 5 21 5 3" />
                                </svg>
                              )}
                            </button>
                            <button
                              className={`${styles.iconBtn} ${styles.dangerBtn}`}
                              onClick={() => handleDeleteProduct(product._id || product.id)}
                              title="Delete Product"
                              aria-label="Delete Product"
                              >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {categoryProducts.length === 0 && (
                    <div className={styles.emptyCategory}>
                      No products in this category
                    </div>
                  )}
                </div>
              );
            })}

            {categories.length === 0 && (
              <div className={styles.empty}>No categories found. Add a category to get started.</div>
            )}
          </div>
        </div>
      </section>

      {/* Tier Modal */}
      <Modal
        isOpen={tierModalOpen}
        onClose={() => setTierModalOpen(false)}
        title={editingTier ? 'Edit Tier' : 'Add Tier'}
        size="medium"
      >
        <form onSubmit={handleTierSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Tier Name</label>
            <Input
              value={tierForm.name}
              onChange={(e) => setTierForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Professional"
              required
            />
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Price ($/month)</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={tierForm.price}
                onChange={(e) => setTierForm(prev => ({ ...prev, price: e.target.value }))}
                placeholder="0.00"
                required
              />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>Description</label>
            <textarea
              className={styles.textarea}
              value={tierForm.description}
              onChange={(e) => setTierForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of this tier..."
              rows={2}
            />
          </div>

          {/* Features checklist */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Features</label>
            <div className={styles.tierFeaturesList}>
              {tierForm.features.map((feature, index, arr) => (
                <div
                  key={index}
                  className={`${styles.tierFeatureItem} ${dragTierFeatureIdx === index ? styles.tierFeatureItemDragging : ''}`}
                  draggable
                  onDragStart={(e) => { setDragTierFeatureIdx(index); e.dataTransfer.effectAllowed = 'move'; }}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragTierFeatureIdx === null || dragTierFeatureIdx === index) return;
                    const items = arr.slice();
                    const [moved] = items.splice(dragTierFeatureIdx, 1);
                    items.splice(index, 0, moved);
                    setTierForm(prev => ({ ...prev, features: items }));
                    setDragTierFeatureIdx(null);
                  }}
                  onDragEnd={() => setDragTierFeatureIdx(null)}
                >
                  <span className={styles.tierFeatureDragHandle}>&#9776;</span>
                  <label className={styles.checkboxLabel}>
                    <Input
                      type="checkbox"
                      checked={feature.isIncluded}
                      onChange={(e) => handleUpdateFeature(index, 'isIncluded', e.target.checked)}
                    />
                  </label>
                  <Input
                    value={feature.featureName}
                    onChange={(e) => handleUpdateFeature(index, 'featureName', e.target.value)}
                    placeholder="Feature name"
                  />
                  <button
                    type="button"
                    className={`${styles.actionBtn} ${styles.danger}`}
                    onClick={() => handleRemoveFeature(index)}
                    style={{ flexShrink: 0 }}
                  >
                    &times;
                  </button>
                </div>
              ))}
              <button
                type="button"
                className={styles.addDosageBtn}
                onClick={handleAddFeature}
              >
                + Add Feature
              </button>
            </div>
          </div>

          <div className={styles.formActions}>
            <Button variant="outline" type="button" onClick={() => setTierModalOpen(false)}>Cancel</Button>
            <Button type="submit">
              {editingTier ? 'Update Tier' : 'Create Tier'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Custom Tier Modal */}
      <Modal
        isOpen={customTierModalOpen}
        onClose={() => setCustomTierModalOpen(false)}
        title={editingCustomTier ? 'Edit Custom Tier' : 'Add Custom Tier'}
        size="large"
      >
        <form onSubmit={handleCustomTierSubmit} className={styles.form}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Plan Code</label>
              <select
                className={styles.select}
                value={`${customTierForm.baseName}||${customTierForm.planType}`}
                onChange={(e) => {
                  const [base, plan] = e.target.value.split('||');
                  setCustomTierForm(prev => ({ ...prev, baseName: base, planType: plan }));
                }}
                required
              >
                <option value="||">Select a plan code...</option>
                <option value="Oval Access||member">Oval Access + Member</option>
                <option value="Oval Access||member_spouse">Oval Access + Spouse</option>
                <option value="Oval Access||member_child">Oval Access + Child</option>
                <option value="Oval Access||family">Oval Access + Family</option>
                <option value="Oval One||member">Oval One + Member</option>
                <option value="Oval One||member_spouse">Oval One + Spouse</option>
                <option value="Oval One||member_child">Oval One + Child</option>
                <option value="Oval One||family">Oval One + Family</option>
                <option value="Oval Plus||member">Oval Plus + Member</option>
                <option value="Oval Plus||member_spouse">Oval Plus + Spouse</option>
                <option value="Oval Plus||member_child">Oval Plus + Child</option>
                <option value="Oval Plus||family">Oval Plus + Family</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Entity (Agency)</label>
              <select
                className={styles.select}
                value={customTierForm.agencyName}
                onChange={(e) => setCustomTierForm(prev => ({ ...prev, agencyName: e.target.value }))}
                required
              >
                <option value="">Select an entity...</option>
                {entities.length === 0 ? (
                  <option disabled>{entitiesError ? 'Entities could not be loaded' : 'No entities available'}</option>
                ) : (
                  entities.map(entity => (
                    <option key={entity.id} value={entity.name}>{entity.name}</option>
                  ))
                )}
                {customTierForm.agencyName && !entities.find(e => e.name === customTierForm.agencyName) && (
                  <option value={customTierForm.agencyName}>{customTierForm.agencyName} (not found)</option>
                )}
              </select>
              {entitiesError && (
                <span style={entityErrorStyle} role="alert">
                  Entities could not be loaded.
                  <button type="button" style={entityRetryStyle} onClick={loadEntities}>
                    Retry
                  </button>
                </span>
              )}
            </div>
          </div>
          {(() => {
            const labels = { member: 'Member', member_spouse: 'Spouse', member_child: 'Child', family: 'Family' };
            const label = labels[customTierForm.planType] || '';
            const displayName = [customTierForm.baseName, label].filter(Boolean).join(' + ');
            const fullTierName = displayName && customTierForm.agencyName ? `${displayName}_${customTierForm.agencyName}` : displayName;
            return fullTierName ? (
              <div style={{ fontSize: '0.8rem', color: 'var(--color-gray-500)', marginBottom: '12px' }}>
                Tier name: <strong>{fullTierName}</strong>{customTierForm.agencyName ? '' : ' (select entity to complete)'}
              </div>
            ) : null;
          })()}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Monthly Price ($)</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={customTierForm.monthlyPrice}
                onChange={(e) => setCustomTierForm(prev => ({ ...prev, monthlyPrice: e.target.value }))}
                placeholder="0.00"
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Revenue ($)</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={customTierForm.revenue}
                onChange={(e) => setCustomTierForm(prev => ({ ...prev, revenue: e.target.value }))}
                placeholder="0.00"
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Product Price Modifier (%)</label>
              <Input
                type="number"
                step="1"
                value={customTierForm.productPriceModifier}
                onChange={(e) => setCustomTierForm(prev => ({ ...prev, productPriceModifier: e.target.value }))}
                placeholder="0 (e.g. 20 for +20%, -15 for -15%)"
              />
            </div>
          </div>

          <div className={styles.checkboxRow}>
            <label className={styles.checkboxLabel}>
              <Input
                type="checkbox"
                checked={customTierForm.consultationFeeIncluded}
                onChange={(e) => setCustomTierForm(prev => ({ ...prev, consultationFeeIncluded: e.target.checked }))}
                fullWidth={false}
              />
              Consultation Fee Included
            </label>
            <label className={styles.checkboxLabel}>
              <Input
                type="checkbox"
                checked={customTierForm.shippingHandlingIncluded}
                onChange={(e) => setCustomTierForm(prev => ({ ...prev, shippingHandlingIncluded: e.target.checked }))}
                fullWidth={false}
              />
              Shipping/Handling Included
            </label>
            <label className={styles.checkboxLabel}>
              <Input
                type="checkbox"
                checked={customTierForm.isActive}
                onChange={(e) => setCustomTierForm(prev => ({ ...prev, isActive: e.target.checked }))}
                fullWidth={false}
              />
              Active
            </label>
          </div>

          {/* Features checklist */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Features</label>
            <div className={styles.tierFeaturesList}>
              {customTierForm.features.map((feature, index, arr) => (
                <div
                  key={index}
                  className={`${styles.tierFeatureItem} ${dragCustomFeatureIdx === index ? styles.tierFeatureItemDragging : ''}`}
                  draggable
                  onDragStart={(e) => { setDragCustomFeatureIdx(index); e.dataTransfer.effectAllowed = 'move'; }}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragCustomFeatureIdx === null || dragCustomFeatureIdx === index) return;
                    const items = arr.slice();
                    const [moved] = items.splice(dragCustomFeatureIdx, 1);
                    items.splice(index, 0, moved);
                    setCustomTierForm(prev => ({ ...prev, features: items }));
                    setDragCustomFeatureIdx(null);
                  }}
                  onDragEnd={() => setDragCustomFeatureIdx(null)}
                >
                  <span className={styles.tierFeatureDragHandle}>&#9776;</span>
                  <label className={styles.checkboxLabel}>
                    <Input
                      type="checkbox"
                      checked={feature.isIncluded}
                      onChange={(e) => handleUpdateCustomFeature(index, 'isIncluded', e.target.checked)}
                    />
                  </label>
                  <Input
                    value={feature.featureName}
                    onChange={(e) => handleUpdateCustomFeature(index, 'featureName', e.target.value)}
                    placeholder="Feature name"
                  />
                  <button
                    type="button"
                    className={`${styles.actionBtn} ${styles.danger}`}
                    onClick={() => handleRemoveCustomFeature(index)}
                    style={{ flexShrink: 0 }}
                  >
                    &times;
                  </button>
                </div>
              ))}
              <button
                type="button"
                className={styles.addDosageBtn}
                onClick={handleAddCustomFeature}
              >
                + Add Feature
              </button>
            </div>
          </div>

          {/* Product Economics */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Product Economics</label>
            <p className={styles.productEconomicsHint}>
              Choose pricing behavior per product. "Price Modifier" uses the tier's global modifier. "Retail Price" uses the product's own retail price.
            </p>
            {(() => {
              const activeProducts = products.filter(p => p.isActive);
              const filteredProducts = econCategoryFilter
                ? activeProducts.filter(p => (p.category?.id || p.category?._id || p.categoryId) === econCategoryFilter)
                : activeProducts;
              return (
                <>
                  <div className={styles.econCategoryFilter}>
                    <label className={styles.label} htmlFor="econCategoryFilter">Filter by Category</label>
                    <select
                      id="econCategoryFilter"
                      value={econCategoryFilter}
                      onChange={(e) => setEconCategoryFilter(e.target.value)}
                      className={styles.econCategorySelect}
                    >
                      <option value="">All Products</option>
                      {categories.filter(c => c.isActive !== false).map(cat => (
                        <option key={cat.id || cat._id} value={cat.id || cat._id}>{cat.name}</option>
                      ))}
                    </select>
                    <span className={styles.econProductCount}>
                      Showing {filteredProducts.length} of {activeProducts.length} products
                    </span>
                    <div className={styles.econBulkActions}>
                      <button
                        type="button"
                        className={styles.econBulkBtn}
                        onClick={() => handleSetEconomicsForProducts(filteredProducts, 'retail')}
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        className={styles.econBulkBtn}
                        onClick={() => handleSetEconomicsForProducts(filteredProducts, 'modifier')}
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                  <div className={styles.productEconomicsList}>
                    {filteredProducts.map(product => {
                      const econEntry = customTierForm.productEconomics.find(e => e.productId === (product.id || product._id));
                      const currentPricingType = econEntry?.pricingType || 'modifier';
                      const isVoid = currentPricingType === 'void';
                      const productRetailPrice = product.retailPrice;
                      const hasModifier = parseFloat(customTierForm.productPriceModifier || 0) !== 0;
                      return (
                        <div key={product.id || product._id} className={styles.productEconomicsItem}>
                          <span className={styles.productEconomicsName}>{product.name}</span>
                          <span className={styles.productEconomicsPrices}>
                            (Oval: {formatEconPrice(deriveEconOvalPrice(product))} / Retail: {formatEconPrice(productRetailPrice)})
                          </span>
                          <div className={styles.productEconomicsControl}>
                            {['modifier', ...(hasModifier ? ['void'] : []), 'retail'].map(ptype => (
                              <button
                                key={ptype}
                                type="button"
                                className={`${styles.econTypeBtn} ${currentPricingType === ptype ? styles.econTypeBtnActive : ''}`}
                                onClick={() => {
                                  const pid = product.id || product._id;
                                  setCustomTierForm(prev => {
                                    const existing = prev.productEconomics.filter(e => e.productId !== pid);
                                    if (ptype !== 'modifier') {
                                      existing.push({ productId: pid, pricingType: ptype });
                                    }
                                    return { ...prev, productEconomics: existing };
                                  });
                                }}
                              >
                                {ptype === 'modifier' ? 'Modifier' : ptype === 'void' ? 'Void' : 'Retail'}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </div>

          <div className={styles.formActions}>
            <Button variant="outline" type="button" onClick={() => setCustomTierModalOpen(false)}>Cancel</Button>
            <Button type="submit">
              {editingCustomTier ? 'Update Custom Tier' : 'Create Custom Tier'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Product Modal */}
      <Modal
        isOpen={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        title={editingProduct ? 'Edit Product' : 'Add Product'}
        size="large"
      >
        <form onSubmit={handleProductSubmit} className={styles.form}>
          {/* Product Status */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Product Status</label>
            <select
              className={styles.statusSelect}
              value={deriveProductStatus(productForm)}
              onChange={(e) => setProductForm(prev => ({ ...prev, ...statusToBooleans(e.target.value) }))}
            >
              <option value="active">Active</option>
              <option value="coming_soon">Coming Soon</option>
              <option value="out_of_stock">Out of Stock</option>
              <option value="hidden">Hidden</option>
            </select>
            <span className={styles.fieldHint}>
              {deriveProductStatus(productForm) === 'active' ? 'Product is visible and available for purchase' :
               deriveProductStatus(productForm) === 'coming_soon' ? 'Product is visible but intake form is blocked' :
               deriveProductStatus(productForm) === 'out_of_stock' ? 'Product is visible but intake form is blocked' :
               'Product is hidden from the public catalog'}
            </span>
          </div>

          {/* Preview URL — shown for non-active products */}
          {editingProduct && ['coming_soon', 'out_of_stock', 'hidden'].includes(deriveProductStatus(productForm)) && (
            <div className={styles.previewUrlSection}>
              <label className={styles.label}>Preview URL</label>
              <p className={styles.previewUrlHint}>Share this URL to allow viewing this product before it goes live. The URL uses the product ID as a token.</p>
              <div className={styles.previewUrlRow}>
                <input
                  type="text"
                  className={styles.input}
                  readOnly
                  value={`${window.location.origin}/products/${productForm.slug || editingProduct.slug}?preview=${editingProduct._id || editingProduct.id}`}
                />
                <button
                  type="button"
                  className={styles.previewCopyBtn}
                  onClick={() => {
                    const url = `${window.location.origin}/products/${productForm.slug || editingProduct.slug}?preview=${editingProduct._id || editingProduct.id}`;
                    if (navigator.clipboard?.writeText) {
                      navigator.clipboard.writeText(url).then(() => toast.success('Preview URL copied'));
                    } else {
                      const textarea = document.createElement('textarea');
                      textarea.value = url;
                      document.body.appendChild(textarea);
                      textarea.select();
                      document.execCommand('copy');
                      document.body.removeChild(textarea);
                      toast.success('Preview URL copied');
                    }
                  }}
                >
                  Copy URL
                </button>
              </div>
            </div>
          )}

          {/* Max Shipment Months (Cold-Chain) */}
          <div className={styles.formGroup}>
            <span className={styles.labelRow}>
              <label className={styles.label}>Max Months Per Shipment</label>
              <CostInfo label="Max Months Per Shipment" tip={COST_RULES.maxMonths} />
            </span>
            <input
              type="number"
              className={styles.input}
              min="1"
              max="12"
              value={productForm.maxSupplyMonths || 12}
              onChange={(e) => setProductForm(prev => ({ ...prev, maxSupplyMonths: parseInt(e.target.value) || 12 }))}
            />
            <span className={styles.helperText}>For cooler/cold-chain meds, set to 3. Default: 12.</span>
          </div>

          {/* Apply Promo Section - Only show when editing */}
          {editingProduct && (
            <div className={styles.promoSection}>
              <label className={styles.label}>Apply System Promo</label>
              {loadingSystemPromos ? (
                <span className={styles.helperText}>Loading promos...</span>
              ) : systemPromosError ? (
                <ListLoadError message={systemPromosError} onRetry={loadSystemPromos} />
              ) : (
                <div className={styles.promoAssignment}>
                  <select
                    className={styles.select}
                    value={editingProduct.productPromoId || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val) {
                        handleAssignPromo(val);
                      } else {
                        handleRemovePromo();
                      }
                    }}
                  >
                    <option value="">No promo assigned</option>
                    {systemPromos.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.description || p.code} — {p.discountType === 'percentage' ? `${p.discountValue}%` : `${(p.discountValue / 100).toFixed(2)}`}
                      </option>
                    ))}
                  </select>
                  {editingProduct.productPromoId && (() => {
                    const promo = systemPromos.find(p => p.id === editingProduct.productPromoId);
                    if (!promo) return null;
                    return (
                      <div className={styles.promoDetails}>
                        <div className={styles.promoDetailName}>{promo.description || promo.code}</div>
                        <div className={styles.promoDetailInfo}>
                          {promo.discountType === 'percentage' ? `${promo.discountValue}% off` : `${(promo.discountValue / 100).toFixed(2)} off`}
                          {promo.applicableMonths && Object.keys(promo.applicableMonths).length > 0 && (
                            <span> — Per month: {Object.entries(promo.applicableMonths).map(([m, pct]) => `Month ${m}: ${pct}% off`).join(', ')}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          className={styles.removePromoBtn}
                          onClick={handleRemovePromo}
                        >
                          Remove Promo
                        </button>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          <div className={styles.formGroup}>
            <label className={styles.label}>Name</label>
            <Input
              value={productForm.name}
              onChange={(e) => setProductForm(prev => ({
                ...prev,
                name: e.target.value,
                slug: prev.slug || e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
              }))}
              placeholder="Product name"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Slug</label>
            <Input
              value={productForm.slug}
              onChange={(e) => setProductForm(prev => ({ ...prev, slug: e.target.value }))}
              placeholder="product-slug"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Category</label>
            <select
              className={styles.select}
              value={productForm.category}
              onChange={(e) => setProductForm(prev => ({ ...prev, category: e.target.value }))}
              required
            >
              <option value="">Select category...</option>
              {categories.map(cat => (
                <option key={cat._id || cat.id} value={cat._id || cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup} style={{flex: 1}}>
              <label className={styles.label}>Active Provider Network</label>
              <div className={styles.networkToggle}>
                <button
                  type="button"
                  className={`${styles.networkToggleOption} ${productForm.providerType !== 'Doctor Now' ? styles.networkToggleActive : ''}`}
                  onClick={() => setProductForm(prev => ({ ...prev, providerType: 'DrCare' }))}
                >
                  DrCare
                </button>
                <button
                  type="button"
                  className={`${styles.networkToggleOption} ${productForm.providerType === 'Doctor Now' ? styles.networkToggleActive : ''}`}
                  onClick={() => setProductForm(prev => ({ ...prev, providerType: 'Doctor Now' }))}
                >
                  DoctorNow
                </button>
              </div>
            </div>
            <div className={styles.formGroup} style={{flex: 1}}>
              <label className={styles.label}>Pharmacy</label>
              <select
                className={styles.select}
                value={productForm.pharmacyName}
                onChange={(e) => setProductForm(prev => ({ ...prev, pharmacyName: e.target.value }))}
              >
                <option value="">—</option>
                <option value="Pharmacy Hub">Pharmacy Hub</option>
                <option value="Belmar">Belmar</option>
              </select>
            </div>
          </div>

          <div className={styles.configTabsWrapper}>
            <div className={styles.configTabsLabel}>Configuration</div>
            <div className={styles.configTabs}>
              <button
                type="button"
                className={`${styles.configTab} ${configTab !== 'Doctor Now' ? styles.configTabActive : ''}`}
                onClick={() => setConfigTab('DrCare')}
              >
                DrCare Settings
              </button>
              <button
                type="button"
                className={`${styles.configTab} ${configTab === 'Doctor Now' ? styles.configTabActive : ''}`}
                onClick={() => setConfigTab('Doctor Now')}
              >
                DoctorNow Settings
              </button>
            </div>
          </div>

            <div className={styles.doctorNowSection} style={{ display: configTab === 'Doctor Now' ? 'block' : 'none' }}>
              <div className={styles.sectionLabel}>DoctorNow Configuration</div>
              <div className={styles.formRow}>
                <div className={styles.formGroup} style={{flex: 1}}>
                  <label className={styles.label}>Encounter Type ID</label>
                  <Input
                    type="text"
                    value={productForm.doctorNowConfig.encounterTypeId}
                    onChange={(e) => setProductForm(prev => ({
                      ...prev,
                      doctorNowConfig: { ...prev.doctorNowConfig, encounterTypeId: e.target.value }
                    }))}
                    placeholder="e.g., 01abc-def..."
                  />
                </div>
                <div className={styles.formGroup} style={{flex: 1}}>
                  <label className={styles.label}>Package ID</label>
                  <Input
                    type="text"
                    value={productForm.doctorNowConfig.packageId}
                    onChange={(e) => setProductForm(prev => ({
                      ...prev,
                      doctorNowConfig: { ...prev.doctorNowConfig, packageId: e.target.value }
                    }))}
                    placeholder="e.g., 019e2bdb-e9e8-7279-b860-ac8830c67f3b"
                    style={{ fontFamily: "'JetBrains Mono', 'SF Mono', monospace", fontSize: '12px' }}
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup} style={{flex: 1}}>
                  <label className={styles.label}>Consult Type</label>
                  <select
                    className={styles.select}
                    value={productForm.doctorNowConfig.consultType}
                    onChange={(e) => setProductForm(prev => ({
                      ...prev,
                      doctorNowConfig: { ...prev.doctorNowConfig, consultType: e.target.value }
                    }))}
                  >
                    <option value="Async">Async</option>
                    <option value="Sync">Sync</option>
                    <option value="Sync-Video">Sync-Video</option>
                  </select>
                </div>
                <div className={styles.formGroup} style={{flex: 1}}>
                  <label className={styles.label}>Intake Form</label>
                  <select
                    className={styles.select}
                    value={productForm.doctorNowConfig.intakeFormId}
                    onChange={(e) => setProductForm(prev => ({
                      ...prev,
                      doctorNowConfig: { ...prev.doctorNowConfig, intakeFormId: e.target.value }
                    }))}
                  >
                    <option value="">Select Intake Form</option>
                    {intakeForms.map((form) => (
                      <option key={form.id} value={form.id}>{form.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Monthly Plans */}
              <div className={styles.sectionLabel} style={{ marginTop: '1.25rem' }}>Monthly Plans</div>
              <div className={styles.dnPlansNote}>
                Reference only — DoctorNow plan authoring. These prices are <strong>not</strong> what
                customers are charged. The charged price is set per dosage
                under <strong>Supply Duration Pricing</strong>, below.
              </div>
              <div className={styles.productPlansSection}>
                <div className={styles.productPlansGrid}>
                  <div className={styles.productPlanHeader}>
                    <span>Month</span>
                    <span>Plan ID (UUID)</span>
                    <span>Base ($)</span>
                    <span>Access ($)</span>
                    <span>ONE ($)</span>
                    <span>PLUS ($)</span>
                  </div>
                  {[1, 3, 6, 12].map(dur => {
                    const durKey = String(dur);
                    const plan = (productForm.doctorNowConfig.plans || {})[durKey] || {};
                    return (
                      <div key={durKey} className={styles.productPlanRow}>
                        <span className={styles.productPlanDuration}>{getSupplyLabel(dur)}</span>
                        <Input
                          value={plan.planId || ''}
                          onChange={(e) => setProductForm(prev => ({
                            ...prev,
                            doctorNowConfig: {
                              ...prev.doctorNowConfig,
                              plans: {
                                ...prev.doctorNowConfig.plans,
                                [durKey]: { ...(prev.doctorNowConfig.plans || {})[durKey], planId: e.target.value }
                              }
                            }
                          }))}
                          placeholder="Plan UUID..."
                          style={{ fontFamily: "'JetBrains Mono', 'SF Mono', monospace", fontSize: '11px' }}
                        />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={plan.basePrice != null ? (plan.basePrice / 100).toFixed(2) : ''}
                          onChange={(e) => setProductForm(prev => ({
                            ...prev,
                            doctorNowConfig: {
                              ...prev.doctorNowConfig,
                              plans: {
                                ...prev.doctorNowConfig.plans,
                                [durKey]: { ...(prev.doctorNowConfig.plans || {})[durKey], basePrice: Math.round(parseFloat(e.target.value || '0') * 100) }
                              }
                            }
                          }))}
                          placeholder="0.00"
                          className={styles.tierPriceInput}
                        />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={plan.accessPrice != null ? (plan.accessPrice / 100).toFixed(2) : ''}
                          onChange={(e) => setProductForm(prev => ({
                            ...prev,
                            doctorNowConfig: {
                              ...prev.doctorNowConfig,
                              plans: {
                                ...prev.doctorNowConfig.plans,
                                [durKey]: { ...(prev.doctorNowConfig.plans || {})[durKey], accessPrice: Math.round(parseFloat(e.target.value || '0') * 100) }
                              }
                            }
                          }))}
                          placeholder="0.00"
                          className={styles.tierPriceInput}
                        />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={plan.onePrice != null ? (plan.onePrice / 100).toFixed(2) : ''}
                          onChange={(e) => setProductForm(prev => ({
                            ...prev,
                            doctorNowConfig: {
                              ...prev.doctorNowConfig,
                              plans: {
                                ...prev.doctorNowConfig.plans,
                                [durKey]: { ...(prev.doctorNowConfig.plans || {})[durKey], onePrice: Math.round(parseFloat(e.target.value || '0') * 100) }
                              }
                            }
                          }))}
                          placeholder="0.00"
                          className={styles.tierPriceInput}
                        />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={plan.plusPrice != null ? (plan.plusPrice / 100).toFixed(2) : ''}
                          onChange={(e) => setProductForm(prev => ({
                            ...prev,
                            doctorNowConfig: {
                              ...prev.doctorNowConfig,
                              plans: {
                                ...prev.doctorNowConfig.plans,
                                [durKey]: { ...(prev.doctorNowConfig.plans || {})[durKey], plusPrice: Math.round(parseFloat(e.target.value || '0') * 100) }
                              }
                            }
                          }))}
                          placeholder="0.00"
                          className={styles.tierPriceInput}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Consultation Fee</label>
            <Input
              type="number"
              value={productForm.consultationFee}
              onChange={(e) => setProductForm(prev => ({ ...prev, consultationFee: e.target.value }))}
              placeholder="e.g., 50.00"
              step="0.01"
              min="0"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Increased Strength Fee ($)</label>
            <Input
              type="number"
              value={productForm.increasedStrengthFee}
              onChange={(e) => setProductForm(prev => ({ ...prev, increasedStrengthFee: e.target.value }))}
              placeholder="e.g., 25.00"
              step="0.01"
              min="0"
            />
            <span className={styles.fieldHint}>Charged as a separate hold on higher-dosage refill requests. Leave empty or 0 for no fee.</span>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup} style={{flex: 1}}>
              {/* The label follows the CONTROL, because this one group renders two different
                  ones: dosage-priced products get the read-only Starting At editor, everything
                  else gets the product's own base (Oval) price input. A single fixed label put
                  "Starting At Prices" over the base-price field on every create and on every
                  dosage-less product — the helper text below it already said "For products
                  without dosages". Units are dollars here, as they are for Consultation Fee. */}
              <span className={styles.labelRow}>
                <label className={styles.label}>{hasDosagePricing ? 'Starting At Prices' : 'Oval Price ($)'}</label>
                {hasDosagePricing && <CostInfo label="Starting At Prices" tip={STARTING_AT_RULES.mode} />}
              </span>
              {hasDosagePricing ? (
                (() => {
                  // Auto prices come from the server engine (startingAtService, via the product API) and are
                  // never recomputed here — one engine, not two, so the modal cannot drift from what the
                  // catalog quotes. computeTierStartingAt() is the fallback for STARTING_AT_BASIS_V2=false,
                  // where the transformer stops emitting startingAt and this page must render as it did before.
                  //
                  // A quote that actually came back from the server. This separates "the server says this basis
                  // has no price" (render No pricing) from "we have not asked yet, or the ask failed" (keep the
                  // last good numbers up) — collapsing the two would light the 6 Month pill over 3 Month prices.
                  const hasQuote = basisPreview.quotedBasis !== null;
                  const autoPrices = hasQuote
                    ? basisPreview.auto
                    : (editingProduct.startingAt?.auto ?? computeTierStartingAt());
                  const isManual = productForm.startingAtMode === 'manual';
                  // Provenance for the numbers on screen. A preview answer describes the requested basis;
                  // with no preview in flight the requested basis IS the saved one, so the loaded product's
                  // own startingAt describes it — the saved-basis pill fires no request, and reading only
                  // basisPreview would leave that state permanently undisclosed.
                  const startingAtSignal = basisPreview.signal ?? editingProduct.startingAt ?? null;
                  // While a quote is loading or has failed, `signal` is still the PREVIOUS basis's answer and
                  // the helper text below already says the prices are the last loaded ones. A disclosure is a
                  // claim about the number on screen; a stale one is the very thing this block exists to stop.
                  const basisDisclosure = isManual || basisPreview.loading || basisPreview.error
                    ? null
                    : deriveBasisDisclosure(startingAtSignal, productForm.startingAtBasis);
                  return (
                    <div className={styles.startingAtBlock}>
                      <span className={styles.labelRow}>
                        <label className={styles.startingAtSubLabel}>Supply Basis</label>
                        <CostInfo label="Supply Basis" tip={STARTING_AT_RULES.basis} />
                      </span>
                      <div className={styles.basisToggle} role="group" aria-label="Supply basis">
                        {STARTING_AT_BASIS_OPTIONS.map(({ value, label }) => (
                          <button
                            key={value}
                            type="button"
                            className={`${styles.basisOption} ${productForm.startingAtBasis === value ? styles.basisOptionActive : ''}`}
                            aria-pressed={productForm.startingAtBasis === value}
                            onClick={() => setProductForm(prev => ({ ...prev, startingAtBasis: value }))}
                          >
                            {label}
                            {value === DEFAULT_STARTING_AT_BASIS && (
                              <span className={styles.basisDefaultStar} aria-label="default">★</span>
                            )}
                          </button>
                        ))}
                      </div>

                      <span className={styles.startingAtSubLabel}>Pricing Mode</span>
                      <div className={styles.networkToggle} role="group" aria-label="Starting at pricing mode">
                        {STARTING_AT_MODE_OPTIONS.map(({ value, label }) => (
                          <button
                            key={value}
                            type="button"
                            className={`${styles.networkToggleOption} ${productForm.startingAtMode === value ? styles.networkToggleActive : ''}`}
                            aria-pressed={productForm.startingAtMode === value}
                            onClick={() => setProductForm(prev => ({ ...prev, startingAtMode: value }))}
                          >
                            {label}
                          </button>
                        ))}
                      </div>

                      {STARTING_AT_TIERS.map(({ key, label }) => (
                        <div key={key} className={styles.startingAtTierRow}>
                          <span className={styles.startingAtTierLabel}>{label}</span>
                          {isManual ? (
                            <Input
                              type="number"
                              className={styles.startingAtTierInput}
                              value={productForm.startingAtManual?.[key] ?? ''}
                              onChange={(e) => setProductForm(prev => ({
                                ...prev,
                                startingAtManual: { ...prev.startingAtManual, [key]: e.target.value }
                              }))}
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                              aria-label={`${label} tier starting at price, dollars per month`}
                            />
                          ) : (
                            <span
                              className={`${styles.startingAtTierValue} ${basisPreview.loading ? styles.startingAtTierValueLoading : ''}`}
                              aria-busy={basisPreview.loading}
                            >
                              {autoPrices?.[key] ? `$${(autoPrices[key] / 100).toFixed(2)}/mo` : 'No pricing'}
                            </span>
                          )}
                        </div>
                      ))}

                      {basisDisclosure && (
                        <span className={styles.basisDisclosure} role="status">
                          {basisDisclosure}
                        </span>
                      )}

                      <span
                        className={`${styles.helperText} ${basisPreview.error ? styles.startingAtError : ''}`}
                        role={basisPreview.error ? 'alert' : undefined}
                      >
                        {isManual
                          ? 'Manual: these exact prices are advertised, with no tier modifier or promo applied.'
                          : basisPreview.loading
                            ? `Auto (read-only): quoting ${getBasisLabel(productForm.startingAtBasis)}...`
                            : basisPreview.error
                              ? `Could not quote ${getBasisLabel(productForm.startingAtBasis)} (${basisPreview.error}) - showing the last loaded prices.`
                              : 'Auto (read-only): cheapest monthly rate per tier, quoted from the basis above. Save to apply it to the catalog.'}
                      </span>
                    </div>
                  );
                })()
              ) : (
                <>
                  <Input
                    type="number"
                    value={productForm.basePrice}
                    onChange={(e) => { setPromoConflictError(null); setProductForm(prev => ({ ...prev, basePrice: e.target.value })); }}
                    placeholder="e.g., 29.99"
                    step="0.01"
                    min="0"
                  />
                  <span className={styles.helperText}>For products without dosages (in dollars)</span>
                </>
              )}
              {promoConflictError && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', color: '#dc2626', fontSize: '0.8rem', fontWeight: 600 }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                    <circle cx="8" cy="8" r="7" stroke="#dc2626" strokeWidth="1.5" fill="none"/>
                    <rect x="7.25" y="4" width="1.5" height="5" rx="0.75" fill="#dc2626"/>
                    <circle cx="8" cy="11.5" r="0.9" fill="#dc2626"/>
                  </svg>
                  <span>Promo Code Conflict: {promoConflictError}</span>
                </div>
              )}
            </div>
            <div className={styles.formGroup} style={{flex: 1}}>
              <label className={styles.label}>Retail Price</label>
              <Input
                type="number"
                value={productForm.retailPrice}
                onChange={(e) => setProductForm(prev => ({ ...prev, retailPrice: e.target.value }))}
                placeholder="e.g., 99.99"
                step="0.01"
                min="0"
              />
              <span className={styles.helperText}>Full retail price (for savings display)</span>
            </div>
          </div>

          <div className={styles.formGroup}>
            <span className={styles.labelRow}>
              <label className={styles.label}>Shipping & Handling Cost</label>
              <CostInfo label="Shipping & Handling Cost" tip={COST_RULES.shipping} />
            </span>
            <Input
              type="number"
              value={productForm.shippingCost}
              onChange={(e) => setProductForm(prev => ({ ...prev, shippingCost: e.target.value }))}
              placeholder="e.g., 9.99"
              step="0.01"
              min="0"
            />
          </div>

          <div className={styles.internalCostsSection}>
            <div className={styles.internalCostsHeader}>
              <label className={styles.label}>Internal Costs</label>
              <span className={styles.helperText}>What Oval pays — not customer-facing</span>
            </div>
            <div className={styles.costFields}>
              <div className={styles.costField}>
                <span className={styles.labelRow}>
                  <span className={styles.costLabel}>Medication Cost</span>
                  <CostInfo label="Medication Cost" tip={COST_RULES.medication} />
                </span>
                <div className={styles.costInput}>
                  <span className={styles.costPrefix}>$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={productForm.medicationCost}
                    onChange={(e) => setProductForm(prev => ({ ...prev, medicationCost: e.target.value }))}
                    className={styles.costInputEl}
                  />
                </div>
              </div>
              <div className={styles.costField}>
                <span className={styles.labelRow}>
                  <span className={styles.costLabel}>Consultation Cost</span>
                  <CostInfo label="Consultation Cost" tip={COST_RULES.consultation} />
                </span>
                <div className={styles.costInput}>
                  <span className={styles.costPrefix}>$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={productForm.consultationCost}
                    onChange={(e) => setProductForm(prev => ({ ...prev, consultationCost: e.target.value }))}
                    className={styles.costInputEl}
                  />
                </div>
              </div>
              <div className={styles.costField}>
                <span className={styles.labelRow}>
                  <span className={styles.costLabel}>Miscellaneous Cost</span>
                  <CostInfo label="Miscellaneous Cost" tip={COST_RULES.misc} />
                </span>
                <div className={styles.costInput}>
                  <span className={styles.costPrefix}>$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={productForm.miscCost}
                    onChange={(e) => setProductForm(prev => ({ ...prev, miscCost: e.target.value }))}
                    className={styles.costInputEl}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Short Description</label>
            <textarea
              className={styles.textarea}
              value={productForm.shortDescription}
              onChange={(e) => setProductForm(prev => ({ ...prev, shortDescription: e.target.value }))}
              placeholder="Brief product description..."
              rows={3}
            />
          </div>

          <div className={styles.formRow}>
            <label className={styles.checkboxLabel}>
              <Input
                type="checkbox"
                checked={productForm.isActive}
                onChange={(e) => setProductForm(prev => ({ ...prev, isActive: e.target.checked }))}
              />
              Active
            </label>
            <label className={styles.checkboxLabel}>
              <Input
                type="checkbox"
                checked={productForm.isPrescriptionRequired}
                onChange={(e) => setProductForm(prev => ({ ...prev, isPrescriptionRequired: e.target.checked }))}
              />
              Prescription Required
            </label>
            <label className={styles.checkboxLabel}>
              <Input
                type="checkbox"
                checked={productForm.isControlledSubstance}
                onChange={(e) => setProductForm(prev => ({ ...prev, isControlledSubstance: e.target.checked }))}
              />
              Controlled Substance
            </label>
          </div>

          {/* Image Upload - Only show when editing */}
          {editingProduct && (
            <div className={styles.formGroup}>
              <label className={styles.label}>Product Images</label>
              <div className={styles.imageUploadSection}>
                  <div className={styles.imageGrid}>
                    {productImages.map((img) => (
                      <div key={img.id} className={`${styles.imageItem} ${img.isPrimary ? styles.primaryImage : ''}`}>
                        <img loading="lazy" src={img.url} alt={img.alt || 'Product'} />
                        <div className={styles.imageOverlay}>
                          {!img.isPrimary && (
                            <button
                              type="button"
                              className={styles.primaryBtn}
                              onClick={() => handleSetPrimaryImage(img.id)}
                              title="Set as primary"
                            >
                              ★
                            </button>
                          )}
                          <button
                            type="button"
                            className={styles.deleteImageBtn}
                            onClick={() => handleDeleteProductImage(img.id)}
                            title="Delete image"
                          >
                            ×
                          </button>
                        </div>
                        {img.isPrimary && <span className={styles.primaryBadge}>Primary</span>}
                      </div>
                    ))}
                    <label className={styles.uploadBtn}>
                      <Input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleProductImageUpload}
                        disabled={uploadingImages}
                        style={{ display: 'none' }}
                      />
                      {uploadingImages ? 'Uploading...' : '+ Add Images'}
                    </label>
                  </div>
              </div>
            </div>
          )}

          {/* Lifestyle Image Upload */}
          {editingProduct && (
            <div className={styles.formGroup}>
              <label className={styles.label}>Lifestyle Image</label>
              <div className={styles.lifestyleUploadSection}>
                {productForm.lifestyleImageUrl ? (
                  <div className={styles.lifestyleImagePreview}>
                    <img
                      src={productForm.lifestyleImageUrl}
                      alt="Lifestyle preview"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                    <button
                      type="button"
                      className={styles.removeLifestyleBtn}
                      onClick={handleDeleteLifestyleImage}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <label
                    className={styles.uploadBtn}
                    style={{
                      border: '2px dashed #d1d5db',
                      borderRadius: '12px',
                      padding: '2rem 1.5rem',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: uploadingLifestyleImage ? 'wait' : 'pointer',
                      transition: 'border-color 0.2s, background-color 0.2s',
                      backgroundColor: 'transparent',
                      textAlign: 'center',
                      minHeight: '120px',
                      justifyContent: 'center'
                    }}
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#f97316'; e.currentTarget.style.backgroundColor = 'rgba(249,115,22,0.05)'; }}
                    onDragLeave={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                    onDrop={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.backgroundColor = 'transparent'; handleLifestyleImageDrop(e); }}
                  >
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleLifestyleImageUpload}
                      disabled={uploadingLifestyleImage}
                      style={{ display: "none" }}
                    />
                    {uploadingLifestyleImage ? "Uploading..." : (
                      <>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        <span style={{ fontWeight: 600, color: '#525252' }}>Drag & drop an image here</span>
                        <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>or click to browse</span>
                      </>
                    )}
                  </label>
                )}
              </div>
            </div>
          )}


          <div className={styles.drCareSection} style={{ display: configTab !== 'Doctor Now' ? 'block' : 'none' }}>
            <div className={styles.sectionLabel}>DrCare Configuration</div>
          {/* DrCare Integration - Specialty & Reason IDs */}
          <div className={styles.formGroup}>
            <label className={styles.label}>DrCare Specialty</label>
            <select
              className={styles.select}
              value={(() => {
                const match = DRCARE_SPECIALTY_OPTIONS.find(o => o.specialtyId === productForm.drcareSpecialtyId);
                return match ? JSON.stringify({ s: match.specialtyId, r: match.reasonId }) : 'custom';
              })()}
              onChange={(e) => {
                if (e.target.value === 'custom') {
                  setProductForm(prev => ({ ...prev, drcareSpecialtyId: '', drcareReasonId: '' }));
                } else {
                  const parsed = JSON.parse(e.target.value);
                  setProductForm(prev => ({ ...prev, drcareSpecialtyId: parsed.s, drcareReasonId: parsed.r || '' }));
                }
              }}
            >
              <option value="custom">-- Select Specialty --</option>
              {DRCARE_SPECIALTY_OPTIONS.map(opt => (
                <option key={opt.specialtyId || 'custom'} value={JSON.stringify({ s: opt.specialtyId, r: opt.reasonId })}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Specialty ID</label>
            <Input
              value={productForm.drcareSpecialtyId}
              onChange={(e) => setProductForm(prev => ({ ...prev, drcareSpecialtyId: e.target.value }))}
              placeholder="UUID — auto-filled from dropdown or enter manually"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Reason ID</label>
            <Input
              value={productForm.drcareReasonId}
              onChange={(e) => setProductForm(prev => ({ ...prev, drcareReasonId: e.target.value }))}
              placeholder="UUID — auto-filled from dropdown or enter manually"
            />
          </div>

          {/* Syringe Section */}
          <div className={styles.formGroup}>
            <label className={styles.label} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              Syringe
              {productForm.syringeCode && (
                <span style={{ fontSize: "12px", color: "#e67e22", fontWeight: 500 }}>Active</span>
              )}
            </label>
            <select
              value={productForm.syringeCode ? productForm.syringeCode + "|||" + productForm.syringeDescription : ""}
              onChange={(e) => {
                if (!e.target.value) {
                  setProductForm(prev => ({ ...prev, syringeCode: "", syringeDescription: "" }));
                } else {
                  const [code, desc] = e.target.value.split("|||");
                  setProductForm(prev => ({ ...prev, syringeCode: code, syringeDescription: desc }));
                }
              }}
              className={styles.select}
              style={{ padding: "8px 12px", borderRadius: "6px", border: "0.5px solid #d0c8b8", background: "#fffdf8", fontSize: "14px" }}
            >
              <option value="">No syringe required</option>
              {syringes.filter(s => s.isActive).map(s => (
                <option key={s.id} value={s.code + "|||" + s.description}>
                  {s.code} — {s.description}{s.fulfillmentQuantity !== 1 ? ` (Qty: ${s.fulfillmentQuantity})` : ''}
                </option>
              ))}
            </select>
            {productForm.syringeCode && (
              <div style={{ fontSize: "12px", color: "#8a7e6b", marginTop: "4px" }}>
                Code: <strong>{productForm.syringeCode}</strong> — {productForm.syringeDescription}
              </div>
            )}
          </div>
          </div>

          {/* Dosages Section - only when editing. NOT gated on configTab: dosages are product data,
              not provider config, and this section hosts the Supply Duration Pricing grid that sets
              the charged price. Gating it hid that grid on every DoctorNow product. */}
          {editingProduct && (
            <div className={styles.formGroup}>
              <label className={styles.label}>{productForm.providerType === 'Doctor Now' ? 'Dosage / Package Options' : 'Dosage Options'}</label>
                <div className={styles.dnInfoBanner}>
                  <span className={styles.dnInfoIcon}>&#9733;</span>
                  <div>
                    <div className={styles.dnInfoTitle}>DoctorNow Plans</div>
                    <div className={styles.dnInfoBody}>
                      Plan ID (subscription term) and Product ID (medication SKU) vary by month duration.
                      Assign both from the DoctorNow dashboard for each term.
                    </div>
                  </div>
                </div>
              <div className={styles.dosagesSection}>
                {loadingDosages ? (
                  <div className={styles.dosagesLoading}>Loading dosages...</div>
                ) : dosagesError ? (
                  <ListLoadError
                    message={dosagesError}
                    onRetry={() => loadProductDosages(editingProduct)}
                  />
                ) : (
                  <>
                    {productDosages.length > 0 && (
                      <div className={styles.dosagesList}>
                        {productDosages.map((dosage) => (
                          <div key={dosage.id} className={styles.dosageItem}>
                            <div style={{flex: 1}}>
                              <div className={styles.dosageFields}>
                                {productForm.providerType !== 'Doctor Now' && (
                                <div className={styles.dosageField}>
                                  <label className={styles.dosageFieldLabel}>Amount</label>
                                  <Input
                                    value={dosage.amount || ''}
                                    onChange={(e) => handleUpdateDosage(dosage.id, { amount: e.target.value })}
                                    placeholder="e.g., 10mg"
                                    className={styles.dosageInput}
                                  />
                                </div>
                                )}
                                <div className={styles.dosageField}>
                                  <label className={styles.dosageFieldLabel}>Description</label>
                                  <Input
                                    value={dosage.description || ''}
                                    onChange={(e) => handleUpdateDosage(dosage.id, { description: e.target.value })}
                                    placeholder="Optional description"
                                    className={styles.dosageInput}
                                  />
                                </div>
                                <div className={styles.dosageField}>
                                  <label className={styles.dosageFieldLabel}>Oval Price</label>
                                  <div style={{ padding: '6px 10px', background: '#f5f0eb', borderRadius: '4px', fontSize: '13px', color: '#2d2d2d', fontWeight: 600, minWidth: '80px' }}>
                                    {(() => { const op = computeOvalPrice(dosage); return op ? `$${(op / 100).toFixed(2)}` : '-'; })()}
                                  </div>
                                  <span style={{ fontSize: '10px', color: '#8a7e6b' }}>Auto</span>
                                </div>
                                <div className={styles.dosageField}>
                                  <label className={styles.dosageFieldLabel}>Retail Price ($)</label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={formatDollars(dosage.retailPrice)}
                                    onChange={(e) => handleUpdateDosage(dosage.id, { retailPrice: parseCents(e.target.value) })}
                                    placeholder="0.00"
                                    className={styles.dosageInput}
                                  />
                                </div>
                                {/* Supply field removed — supply durations managed via Supply Duration Pricing grid below */}
                              </div>
                              <div className={styles.dosageFieldsRow2}>
                                <div className={styles.dosageField} style={{flex: 1, maxWidth: '180px'}}>
                                  <label className={styles.dosageFieldLabel}>NDC Code</label>
                                  <Input
                                    value={dosage.ndcCode || ''}
                                    onChange={(e) => handleUpdateDosage(dosage.id, { ndcCode: e.target.value })}
                                    placeholder="e.g. 76282-643"
                                    className={styles.dosageInput}
                                  />
                                </div>
                                <div className={styles.dosageField} style={{flex: 1, maxWidth: '140px'}}>
                                  <label className={styles.dosageFieldLabel}>Fulfillment Qty</label>
                                  <Input
                                    type="number"
                                    step="0.1"
                                    min="0.1"
                                    value={dosage.fulfillmentQuantity ?? 1}
                                    onChange={(e) => handleUpdateDosage(dosage.id, { fulfillmentQuantity: parseFloat(e.target.value) || 1 })}
                                    placeholder="1"
                                    className={styles.dosageInput}
                                    title="Quantity sent to fulfillment per month. e.g. 2.5 mL vial = 2.5. Multiplied by supply months."
                                  />
                                </div>
                                <div className={styles.dosageField}>
                                  <label className={styles.dosageFieldLabel}>Active</label>
                                  <div className={styles.dosageToggle}>
                                    <input
                                      type="checkbox"
                                      checked={dosage.isActive !== false}
                                      onChange={(e) => handleUpdateDosage(dosage.id, { isActive: e.target.checked })}
                                      className={styles.dosageCheckbox}
                                    />
                                  </div>
                                </div>
                                <div className={styles.dosageField}>
                                  <label className={styles.dosageFieldLabel}>Default</label>
                                  <div className={styles.dosageRadio}>
                                    <Input
                                      type="radio"
                                      name="defaultDosage"
                                      checked={dosage.isDefault}
                                      onChange={() => handleSetDefaultDosage(dosage.id)}
                                    />
                                  </div>
                                </div>
                              </div>
                              {/* Supply Duration Pricing — Tier Toggle (visible in both DrCare and DoctorNow) */}
                              <div className={styles.supplyPricingSection}>
                                <div className={styles.supplyPricingHeader}>
                                  {/* labelRow is position:relative — the tip is absolutely
                                      positioned, so without it the tip anchors to a distant
                                      ancestor and renders outside the modal body's clipping box. */}
                                  <span className={styles.labelRow}>
                                    <label className={styles.dosageFieldLabel}>Supply Duration Pricing</label>
                                    <CostInfo label="Supply Duration Pricing" tip={PRICE_RULES.supplyDuration} />
                                  </span>
                                  {(!dosage.supplyPricing || Object.keys(dosage.supplyPricing).length === 0) && (
                                    <button
                                      type="button"
                                      className={styles.addSupplyRowBtn}
                                      onClick={() => handleInitSupplyPricing(dosage.id, dosage.price)}
                                      style={{ marginLeft: '8px' }}
                                    >
                                      Initialize Pricing
                                    </button>
                                  )}
                                </div>
                                <div className={styles.chargedPriceNote}>
                                  <strong>This is the price customers are charged.</strong> Set per dosage, per supply
                                  duration, and per membership tier.
                                </div>
                                {/* The same grid is also the basis of the public "starting at" quote — the
                                    ambiguity that let $69/mo be authored into a grid that charges nothing while
                                    the charging grid kept quoting $132/mo. Copy-only. Aug 4, 2026 — Task 16. */}
                                <div className={styles.startingAtBasisNote}>
                                  <strong>These rates are also the &ldquo;starting at&rdquo; price.</strong> What you type
                                  here is what customers are charged, and the{' '}
                                  <strong>Starting At Prices</strong> block above quotes it at the selected Supply Basis
                                  duration &mdash; unless that block is set to Manual, which overrides it.
                                </div>
                                {dosage.supplyPricing && Object.keys(dosage.supplyPricing).length > 0 && (
                                  <>
                                    <div className={styles.tierToggle}>
                                      {[{ key: 'access', label: 'Access' }, { key: 'one', label: 'ONE' }, { key: 'plus', label: 'PLUS' }].map(({ key, label }) => (
                                        <button
                                          key={key}
                                          type="button"
                                          className={`${styles.tierToggleBtn} ${supplyPricingTier === key ? styles.tierToggleBtnActive : ''}`}
                                          onClick={() => setSupplyPricingTier(key)}
                                        >
                                          {label}
                                        </button>
                                      ))}
                                    </div>
                                    {/* Names the tier that carries a product_price_modifier and by how
                                        much. Renders only for a tier that has one — never invented, never
                                        hardcoded. Aug 4, 2026 — Task 17. */}
                                    {hasPriceModifier && (
                                      <span className={styles.modifierPill}>
                                        {`${activeTierModifier.tierName} ${formatModifierPercent(activeTierModifier.modifier)} price modifier`}
                                      </span>
                                    )}
                                    <span className={styles.fieldHint}>Saves to the selected tier only — Access, ONE and PLUS are priced separately.</span>
                                    <div className={styles.supplyPricingGrid}>
                                      <div className={`${styles.supplyGridHeader} ${productForm.providerType !== 'Doctor Now' ? styles.supplyGridNoCosts : ''}`}>
                                        <span>Duration</span>
                                        <span>Price / Month</span>
                                        <span>Total</span>
                                        {productForm.providerType === 'Doctor Now' && (
                                          <>
                                        <span title={`Admin-only medication cost for this duration. ${COST_RULES.override}`}>Our Cost</span>
                                        <span title={`Admin-only consultation cost for this duration. ${COST_RULES.override}`}>Consult Cost</span>
                                        <span title={`Admin-only shipping & handling cost for this duration. ${COST_RULES.override}`}>S&amp;H</span>
                                          </>
                                        )}
                                        <span>Default</span>
                                        <span>Label</span>
                                        <span>Enabled</span>
                                      </div>
                                      {SUPPLY_DURATIONS.map(months => {
                                        const baseSp = dosage.supplyPricing || {};
                                        const tierCents = getTierPrice(baseSp, months, supplyPricingTier);
                                        const meta = getSupplyMeta(dosage, months);
                                        const totalCents = (tierCents || 0) * months;
                                        // pricingEngine charges the stored value x the RAW key, so a term whose
                                        // internalMonths differs from its key is charged ONCE and is therefore not
                                        // a per-month rate - which is exactly what the "Price / Month" header
                                        // claims. Today that is the 60 Day term alone; deriving it from the term
                                        // table keeps the exception structural rather than a hardcoded key.
                                        const term = getSupplyTerm(String(months));
                                        const isChargedOnce = term.internalMonths !== months;
                                        const tierPricing = baseSp[String(months)];
                                        const hasPricing = tierPricing && typeof tierPricing === 'object' && (tierPricing.access != null || tierPricing.one != null || tierPricing.plus != null);
                                        const isEnabled = meta.enabled !== false && hasPricing;
                                        return (
                                          <div key={months} className={`${styles.supplyGridRow} ${!isEnabled ? styles.supplyGridRowDisabled : ''} ${productForm.providerType !== 'Doctor Now' ? styles.supplyGridNoCosts : ''}`}>
                                            <span className={styles.supplyDurationLabel}>{SUPPLY_LABELS[months]}</span>
                                            {/* The authored rate, and under it the rate this tier is actually
                                                billed once its price modifier is applied. Display only — the
                                                input's value and onChange are untouched. Task 17. */}
                                            <div className={styles.supplyPriceCell}>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              min="0"
                                              value={tierCents != null ? formatDollars(tierCents) : ''}
                                              onChange={(e) => handleSupplyPricingChange(dosage.id, dosage.supplyPricing, months, supplyPricingTier, e.target.value ? parseCents(e.target.value) : null)}
                                              placeholder="0.00"
                                              className={styles.supplyPriceInput}
                                            />
                                            {/* !== null, not != null: getTierPrice normalises undefined to
                                                null (`entry[tier] ?? null`), so the strict form is exactly
                                                equivalent here and keeps the file's ESLint count flat. */}
                                            {hasPriceModifier && tierCents !== null && (
                                              <span
                                                className={styles.effectiveRatePill}
                                                title={`${activeTierModifier.tierName} pays ${formatModifierPercent(activeTierModifier.modifier)} on this rate`}
                                              >
                                                {`${formatModifierPercent(activeTierModifier.modifier)} → $${formatDollars(applyPriceModifier(tierCents, activeTierModifier.modifier))}`}
                                              </span>
                                            )}
                                            {/* The column header reads "Price / Month" for all four rows, but this
                                                one holds a full-term total charged once. Name the implied per-month
                                                rate - the same figure the customer surfaces render after cards
                                                2845/2846 - so admin and customer agree. Display only: totalCents and
                                                the input are untouched. Aug 27, 2026 - Task 2850. */}
                                            {isChargedOnce && tierCents !== null && (
                                              <span
                                                className={styles.chargedOncePill}
                                                title={PRICE_RULES.supplyDuration}
                                              >
                                                {`charged once \u2014 $${formatDollars(toPerMonthRate(tierCents, String(months)))}/mo \u00d7 ${term.internalMonths}`}
                                              </span>
                                            )}
                                            </div>
                                            <span className={styles.supplyTotal}>{tierCents ? formatDollars(totalCents) : '-'}</span>
                                            {productForm.providerType === 'Doctor Now' && (
                                            <>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              min="0"
                                              value={meta.ourCost != null ? formatDollars(meta.ourCost) : ''}
                                              onChange={(e) => handleDnOurCostChange(dosage.id, dosage.supplyPricingMeta, months, e.target.value ? parseCents(e.target.value) : null)}
                                              placeholder="0.00"
                                              className={styles.supplyPriceInput}
                                              title={`Admin-only medication cost for this duration. ${COST_RULES.override}`}
                                            />
                                            <Input
                                              type="number"
                                              step="0.01"
                                              min="0"
                                              value={meta.consultationCost != null ? formatDollars(meta.consultationCost) : ''}
                                              onChange={(e) => handleDnConsultationCostChange(dosage.id, dosage.supplyPricingMeta, months, e.target.value ? parseCents(e.target.value) : null)}
                                              placeholder="0.00"
                                              className={styles.supplyPriceInput}
                                              title={`Admin-only consultation cost for this duration. ${COST_RULES.override}`}
                                            />
                                            <Input
                                              type="number"
                                              step="0.01"
                                              min="0"
                                              value={meta.shippingAndHandling != null ? formatDollars(meta.shippingAndHandling) : ''}
                                              onChange={(e) => handleDnShippingCostChange(dosage.id, dosage.supplyPricingMeta, months, e.target.value ? parseCents(e.target.value) : null)}
                                              placeholder="0.00"
                                              className={styles.supplyPriceInput}
                                              title={`Admin-only shipping & handling cost for this duration. ${COST_RULES.override}`}
                                            />
                                            </>
                                            )}
                                            <input
                                              type="radio"
                                              name={`defaultSupply_${dosage.id}`}
                                              checked={meta.isDefault === true}
                                              onChange={() => handleSupplyMetaChange(dosage.id, dosage.supplyPricingMeta, months, 'isDefault', true)}
                                            />
                                            <Input
                                              value={meta.label || ''}
                                              onChange={(e) => handleSupplyMetaChange(dosage.id, dosage.supplyPricingMeta, months, 'label', e.target.value)}
                                              placeholder={months === 3 ? 'Best Value' : ''}
                                              className={styles.supplyLabelInput}
                                            />
                                            <input
                                              type="checkbox"
                                              checked={isEnabled}
                                              onChange={(e) => handleSupplyMetaChange(dosage.id, dosage.supplyPricingMeta, months, 'enabled', e.target.checked)}
                                              className={styles.supplyCheckbox}
                                            />
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              className={styles.dosageDeleteBtn}
                              onClick={() => handleDeleteDosage(dosage.id)}
                              title="Delete dosage"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <button
                      type="button"
                      className={styles.addDosageBtn}
                      onClick={handleAddDosage}
                      disabled={addingDosage}
                    >
                      + Add Dosage Option
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          <div className={styles.formActions}>
            <Button type="button" variant="outline" onClick={() => setProductModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit">
              {editingProduct ? 'Update Product' : 'Create Product'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Category Modal */}
      <Modal
        isOpen={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        title={editingCategory ? 'Edit Category' : 'Add Category'}
        size="medium"
      >
        <form onSubmit={handleCategorySubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Name</label>
            <Input
              value={categoryForm.name}
              onChange={(e) => setCategoryForm(prev => ({
                ...prev,
                name: e.target.value,
                slug: prev.slug || e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
              }))}
              placeholder="Category name"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Slug</label>
            <Input
              value={categoryForm.slug}
              onChange={(e) => setCategoryForm(prev => ({ ...prev, slug: e.target.value }))}
              placeholder="category-slug"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Icon (emoji)</label>
            <Input
              value={categoryForm.icon}
              onChange={(e) => setCategoryForm(prev => ({ ...prev, icon: e.target.value }))}
              placeholder="📁"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Short Description</label>
            <textarea
              className={styles.textarea}
              value={categoryForm.shortDescription}
              onChange={(e) => setCategoryForm(prev => ({ ...prev, shortDescription: e.target.value }))}
              placeholder="Brief category description..."
              rows={3}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Default Intake Form</label>
            <select
              className={styles.select}
              value={categoryForm.intakeFormId}
              onChange={(e) => setCategoryForm(prev => ({ ...prev, intakeFormId: e.target.value }))}
            >
              <option value="">None (products must set their own)</option>
              {intakeForms.map((form) => (
                <option key={form.id || form._id} value={form.id || form._id}>
                  {form.name}
                </option>
              ))}
            </select>
            <span className={styles.fieldHint}>Products in this category will use this form unless they have their own.</span>
          </div>

          <div className={styles.formRow}>
            <label className={styles.checkboxLabel}>
              <Input
                type="checkbox"
                checked={categoryForm.isActive}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, isActive: e.target.checked }))}
              />
              Active (visible on catalog)
            </label>
          </div>

          {/* Category Image - Only show when editing */}
          {editingCategory && (
            <div className={styles.formGroup}>
              <label className={styles.label}>Category Image</label>
              <div className={styles.imageUploadSection}>
                {categoryForm.image && (
                  <div className={styles.categoryImagePreview}>
                    <img loading="lazy" src={categoryForm.image} alt="Category" />
                    <button
                      type="button"
                      className={styles.deleteImageBtn}
                      onClick={handleDeleteCategoryImage}
                      title="Delete image"
                    >
                      ×
                    </button>
                  </div>
                )}
                <label className={styles.uploadBtn}>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleCategoryImageUpload}
                    disabled={uploadingCategoryImage}
                    style={{ display: 'none' }}
                  />
                  {uploadingCategoryImage ? 'Uploading...' : categoryForm.image ? 'Change Image' : '+ Add Image'}
                </label>
              </div>
            </div>
          )}

          <div className={styles.formActions}>
            <Button type="button" variant="outline" onClick={() => setCategoryModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit">
              {editingCategory ? 'Update Category' : 'Create Category'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Intake Form Editor Modal */}
      <IntakeFormEditorModal
        isOpen={intakeModalOpen}
        onClose={handleCloseIntakeModal}
        productId={editingIntakeProduct?._id || editingIntakeProduct?.id}
        productName={editingIntakeProduct?.name}
        onSave={handleIntakeFormSave}
      />

      {/* Product Offer Editor Modal */}
      <ProductOfferEditorModal
        isOpen={productOfferModalOpen}
        onClose={handleCloseProductOfferModal}
        product={editingProductForOffer}
        existingOffer={productOffers[editingProductForOffer?._id || editingProductForOffer?.id]}
        onSave={handleProductOfferSave}
      />

      {/* Info Popup Editor Modal */}
      {infoPopupModalOpen && (
        <div className={styles.overlay} onClick={() => setInfoPopupModalOpen(false)}>
          <div className={styles.infoPopupModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.infoPopupHeader}>
              <h2>Edit Info Popup — {infoPopupProduct?.name}</h2>
              <button className={styles.closeBtn} onClick={() => setInfoPopupModalOpen(false)}>×</button>
            </div>
            <div className={styles.infoPopupBody}>
              <div className={styles.infoPopupEditor}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Lifestyle Image</label>
                  <div className={styles.lifestyleImageDisplay}>
                    {infoPopupForm.lifestyleImageUrl ? (
                      <img
                        src={infoPopupForm.lifestyleImageUrl}
                        alt="Lifestyle"
                        className={styles.lifestyleImg}
                      />
                    ) : (
                      <div className={styles.lifestylePlaceholder}>
                        <span>No lifestyle image</span>
                        <small>Add via Product Edit</small>
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.pricePreviewSection}>
                  <label className={styles.label}>Price Preview</label>
                  <div className={styles.pricePreviewGrid}>
                    <div className={styles.pricePreviewItem}>
                      <span className={styles.pricePreviewLabel}>Retail Price</span>
                      <span className={styles.pricePreviewValue}>
                        {infoPopupForm.retailPrice && parseFloat(infoPopupForm.retailPrice) > 0
                          ? `$${parseFloat(infoPopupForm.retailPrice).toFixed(2)}`
                          : 'Not set'}
                      </span>
                    </div>
                    <div className={styles.pricePreviewItem}>
                      <span className={styles.pricePreviewLabel}>Oval Price</span>
                      <span className={styles.pricePreviewValue}>
                        {infoPopupForm.basePrice && parseFloat(infoPopupForm.basePrice) > 0
                          ? `$${parseFloat(infoPopupForm.basePrice).toFixed(2)}`
                          : 'Not set'}
                      </span>
                    </div>
                    {infoPopupForm.retailPrice && parseFloat(infoPopupForm.retailPrice) > 0 &&
                     infoPopupForm.basePrice && parseFloat(infoPopupForm.basePrice) > 0 && (
                      <div className={styles.pricePreviewItem}>
                        <span className={styles.pricePreviewLabel}>Customer Savings</span>
                        <span className={styles.pricePreviewSavings}>
                          {`$${(parseFloat(infoPopupForm.retailPrice) - parseFloat(infoPopupForm.basePrice)).toFixed(2)}`}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Detailed Description</label>
                  <LazyQuill
                    value={infoPopupForm.detailedDescription}
                    onChange={(val) => setInfoPopupForm(prev => ({ ...prev, detailedDescription: val }))}
                  />
                </div>
              </div>
              <div className={styles.infoPopupPreview}>
                <label className={styles.label}>Preview</label>
                <div className={styles.previewCard}>
                  <h3 className={styles.previewTitle}>{infoPopupProduct?.name}</h3>
                  {infoPopupForm.lifestyleImageUrl && (
                    <img
                      src={infoPopupForm.lifestyleImageUrl}
                      alt="Lifestyle"
                      className={styles.previewImage}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div
                    className={styles.previewContent}
                    dangerouslySetInnerHTML={{ __html: infoPopupForm.detailedDescription || '<p style="color:#a3a3a3">No description yet...</p>' }}
                  />
                </div>
              </div>
            </div>
            <div className={styles.infoPopupFooter}>
              <Button variant="secondary" onClick={() => setInfoPopupModalOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveInfoPopup} disabled={savingInfoPopup}>
                {savingInfoPopup ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProductsPage;
